import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, Copy, Check, ChevronDown, ChevronRight, Lock, Unlock, Clock, Terminal, Code, BookOpen, Eye, EyeOff, Menu, X, ArrowLeft, AlertTriangle, Shield } from 'lucide-react';
import { api } from '../../api/client';

// Feature flag: show/hide the "Practice Management Integration" category in the API docs
const PRACTICE_MANAGEMENT_INTEGRATION_ENABLED = process.env.REACT_APP_PRACTICE_MANAGEMENT_INTEGRATION !== 'false';

// API Endpoints Configuration
const API_ENDPOINTS = [
  {
    id: 'credentials',
    category: 'Authentication',
    name: 'Get Live Credentials',
    method: 'POST',
    path: '/api/developer/credentials',
    description: 'Generate or retrieve your Live Client ID and Secret for live data access.',
    auth: 'none',
    environment: 'production',
    requestBody: {
      email: { type: 'string', required: true, example: 'developer@example.com' },
      password: { type: 'string', required: true, example: 'your_password', isPassword: true }
    },
    responseExample: {
      client_id: 'cli-den-abc123def456789...',
      client_secret: 'sec-den-xyz789abc123...',
      message: 'IMPORTANT: This is the only time your Client Secret will be displayed. Please save it securely now. Use these credentials to generate access tokens via POST /api/developer/token.'
    }
  },
  {
    id: 'regenerate-secret',
    category: 'Authentication',
    name: 'Regenerate Live Secret',
    method: 'POST',
    path: '/api/developer/credentials/regenerate',
    description: 'Regenerate your Live Client Secret. Client ID remains the same. All existing tokens will be revoked.',
    auth: 'none',
    environment: 'production',
    requestBody: {
      email: { type: 'string', required: true, example: 'developer@example.com' },
      password: { type: 'string', required: true, example: 'your_password', isPassword: true }
    },
    responseExample: {
      client_id: 'cli-den-abc123def456789...',
      client_secret: 'sec-den-NEW_secret_here...',
      message: 'IMPORTANT: This is the only time your new Client Secret will be displayed. Please save it securely now. All existing tokens have been revoked.'
    }
  },
  {
    id: 'sandbox-credentials',
    category: 'Authentication',
    name: 'Get Sandbox Credentials',
    method: 'POST',
    path: '/api/sandbox/credentials',
    description: 'Generate or retrieve your Sandbox Client ID and Secret for testing with staging data. Secret expires in 30 days.',
    auth: 'none',
    environment: 'sandbox',
    requestBody: {
      email: { type: 'string', required: true, example: 'developer@example.com' },
      password: { type: 'string', required: true, example: 'your_password', isPassword: true }
    },
    responseExample: {
      client_id: 'cli-sandbox-abc123def456789...',
      client_secret: 'sec-sandbox-xyz789abc123...',
      expires_at: '2026-07-18T00:00:00Z',
      message: 'IMPORTANT: This is the only time your Client Secret will be displayed. Please save it securely now. Sandbox credentials are valid for 30 days. Use POST /api/sandbox/token to generate an access token.'
    }
  },
  {
    id: 'sandbox-regenerate-secret',
    category: 'Authentication',
    name: 'Regenerate Sandbox Secret',
    method: 'POST',
    path: '/api/sandbox/credentials/regenerate',
    description: 'Regenerate your Sandbox Client Secret. New secret valid for 30 days. Sandbox Client ID remains the same.',
    auth: 'none',
    environment: 'sandbox',
    requestBody: {
      email: { type: 'string', required: true, example: 'developer@example.com' },
      password: { type: 'string', required: true, example: 'your_password', isPassword: true }
    },
    responseExample: {
      client_id: 'cli-sandbox-abc123def456789...',
      client_secret: 'sec-sandbox-NEW_secret...',
      expires_at: '2026-07-18T00:00:00Z',
      message: 'IMPORTANT: This is the only time your new Client Secret will be displayed. Please save it securely now. Sandbox credentials are valid for 30 days. All existing sandbox tokens have been revoked.'
    }
  },
  {
    id: 'sandbox-token',
    category: 'Authentication',
    name: 'Generate Access Token',
    method: 'POST',
    path: '/api/sandbox/token',
    description: 'Generate a sandbox access token using your Sandbox Client ID and Secret. Token expires in 30 minutes.',
    auth: 'none',
    environment: 'sandbox',
    requestBody: {
      client_id: { type: 'string', required: true, example: 'cli-sandbox-abc123...' },
      client_secret: { type: 'string', required: true, example: 'sec-sandbox-xyz789...' }
    },
    responseExample: {
      access_token: 'sandbox-dev-den-abc123xyz789...',
      token_type: 'Bearer',
      expires_in: 1800,
      expires_at: '2026-05-29T12:30:00Z'
    }
  },
  {
    id: 'token',
    category: 'Authentication',
    name: 'Generate Access Token',
    method: 'POST',
    path: '/api/developer/token',
    description: 'Generate a live access token using your Live Client ID and Secret. Token expires in 30 minutes.',
    auth: 'none',
    environment: 'production',
    requestBody: {
      client_id: { type: 'string', required: true, example: 'cli-den-abc123...' },
      client_secret: { type: 'string', required: true, example: 'sec-den-xyz789...' }
    },
    responseExample: {
      access_token: 'dev-den-abc123xyz789...',
      token_type: 'Bearer',
      expires_in: 1800,
      expires_at: '2026-05-29T12:30:00Z'
    }
  },
  {
    id: 'sessions',
    category: 'Sessions',
    name: 'List Active Sessions',
    method: 'GET',
    path: '/api/developer/sessions',
    description: 'Get a list of all active sessions for your account, including environment type and API logs URL.',
    auth: 'token',
    requestBody: null,
    responseExample: {
      sessions: [
        {
          id: 1,
          created_at: '2026-05-29T10:00:00Z',
          expires_at: '2026-05-29T10:30:00Z',
          last_used_at: '2026-05-29T10:15:00Z',
          ip_address: '192.168.1.1',
          user_agent: 'Chrome on Windows',
          environment: 'production',
          api_logs_url: 'https://bucket.s3.amazonaws.com/developer-api-logs/...',
          is_active: true
        }
      ],
      total: 1
    }
  },
  {
    id: 'revoke-session',
    category: 'Sessions',
    name: 'Revoke Session',
    method: 'DELETE',
    path: '/api/developer/sessions/{session_id}',
    description: 'Revoke a specific session by ID.',
    auth: 'token',
    pathParams: {
      session_id: { type: 'integer', required: true, example: '1' }
    },
    requestBody: null,
    responseExample: {
      message: 'Session revoked successfully'
    }
  },
  {
    id: 'revoke-all',
    category: 'Sessions',
    name: 'Revoke All Sessions',
    method: 'DELETE',
    path: '/api/developer/sessions',
    description: 'Revoke all sessions. You will need to generate a new access token.',
    auth: 'token',
    requestBody: null,
    responseExample: {
      message: 'All sessions have been revoked. Generate a new token using your client credentials.'
    }
  },
  {
    id: 'appointment-types',
    category: 'Appointments',
    name: 'Get Appointment Types',
    method: 'GET',
    path: '/api/developer/appointment-types',
    description: 'Fetch all appointment types from the Dentulu legacy system.',
    auth: 'token',
    requestBody: null,
    responseExample: {
      success: true,
      data: [
        { id: 1, name: 'General Checkup', duration: 30 },
        { id: 2, name: 'Cleaning', duration: 45 }
      ],
      message: 'Appointment types retrieved successfully'
    }
  },
  {
    id: 'procedure-types',
    category: 'Appointments',
    name: 'Get Procedure Types',
    method: 'GET',
    path: '/api/developer/procedure-types',
    description: 'Fetch all procedure types from the Dentulu legacy system.',
    auth: 'token',
    requestBody: null,
    responseExample: {
      success: true,
      data: [
        { id: 1, name: 'Root Canal', code: 'D3310' },
        { id: 2, name: 'Extraction', code: 'D7140' }
      ],
      message: 'Procedure types retrieved successfully'
    }
  },
  {
    id: 'visit-types',
    category: 'Appointments',
    name: 'Get Visit Types',
    method: 'GET',
    path: '/api/developer/visit-types',
    description: 'Fetch all visit types from the Dentulu legacy system.',
    auth: 'token',
    requestBody: null,
    responseExample: {
      success: true,
      data: [
        { id: 1, name: 'New Patient', code: 'NP' },
        { id: 2, name: 'Follow Up', code: 'FU' }
      ],
      message: 'Visit types retrieved successfully'
    }
  },
  {
    id: 'patient-list',
    category: 'Patients',
    name: 'Get Patient List',
    method: 'POST',
    path: '/api/developer/patients/list',
    description: 'Fetch patient list from the Dentulu legacy system with filtering options.',
    auth: 'token',
    requestBody: {
      subscriber_id: { type: 'string', required: true, example: '16118' },
      timeZone: { type: 'string', required: true, example: 'Asia/Kolkata' },
      loadAll: { type: 'boolean', required: true, example: true },
      limitStart: { type: 'string', required: false, example: '0' },
      keyword: { type: 'string', required: false, example: 'Test' }
    },
    responseExample: {
      success: true,
      data: [
        { id: 1, name: 'John Doe', email: 'john@example.com' },
        { id: 2, name: 'Jane Smith', email: 'jane@example.com' }
      ],
      message: 'Patient list retrieved successfully'
    }
  },
  {
    id: 'providers-list',
    category: 'Providers',
    name: 'Get Providers List',
    method: 'POST',
    path: '/api/developer/providers/list',
    description: 'Fetch providers list from the Dentulu legacy system.',
    auth: 'token',
    requestBody: {
      subscriber_id: { type: 'string', required: true, example: '16118' }
    },
    responseExample: {
      success: true,
      data: [
        { id: 1, name: 'Dr. Smith', specialty: 'General Dentist' },
        { id: 2, name: 'Dr. Johnson', specialty: 'Orthodontist' }
      ],
      message: 'Providers list retrieved successfully'
    }
  },
  {
    id: 'provider-availability',
    category: 'Providers',
    name: 'Get Provider Availability',
    method: 'POST',
    path: '/api/developer/providers/availability',
    description: 'Fetch provider availability from the Dentulu legacy system.',
    auth: 'token',
    requestBody: {
      dentulu_provider_id: { type: 'string', required: true, example: 'SzNqYitFdz0' },
      appointment_type: { type: 'string', required: true, example: '5' },
      timeZone: { type: 'string', required: true, example: 'Asia/Kolkata' }
    },
    responseExample: {
      success: true,
      data: {
        available_slots: [
          { date: '2026-06-02', time: '09:00 AM' },
          { date: '2026-06-02', time: '10:00 AM' }
        ]
      },
      message: 'Provider availability retrieved successfully'
    }
  },
  {
    id: 'appointment-services',
    category: 'Appointments',
    name: 'Get Appointment Services',
    method: 'POST',
    path: '/api/developer/appointments/services',
    description: 'Fetch appointment services from the Dentulu legacy system.',
    auth: 'token',
    requestBody: {
      subscriber_id: { type: 'string', required: true, example: '16118' }
    },
    responseExample: {
      success: true,
      data: [
        { id: 1, name: 'Teeth Cleaning', duration: 30 },
        { id: 2, name: 'Root Canal', duration: 60 }
      ],
      message: 'Appointment services retrieved successfully'
    }
  },
  {
    id: 'patient-locations',
    category: 'Patients',
    name: 'Get Patient Locations',
    method: 'POST',
    path: '/api/developer/patients/locations',
    description: 'Fetch patient locations from the Dentulu legacy system.',
    auth: 'token',
    requestBody: {
      dentulu_patient_id: { type: 'string', required: true, example: 'S0h6Vi8wdz0' }
    },
    responseExample: {
      success: true,
      data: [
        { id: 1, name: 'Main Office', address: '123 Main St' },
        { id: 2, name: 'Branch Office', address: '456 Oak Ave' }
      ],
      message: 'Patient locations retrieved successfully'
    }
  },
  {
    id: 'patient-offices',
    category: 'Patients',
    name: 'Get Dental Offices',
    method: 'GET',
    path: '/api/developer/patients/offices',
    description: 'Fetch the list of dental offices allowed for the authenticated user.',
    auth: 'token',
    requestBody: null,
    responseExample: {
      success: true,
      data: [
        {
          dentulu_office_id: 'S0gzYStrdz0',
          email: 'office@example.com',
          office_name: 'Main Office',
          mobile: '5551234567',
          administrator_name: 'Jane Doe'
        }
      ],
      message: 'Allowed dental offices retrieved successfully'
    }
  },
  {
    id: 'patient-create',
    category: 'Patients',
    name: 'Create Patient',
    method: 'POST',
    path: '/api/developer/patients/create',
    description: 'Create a new patient in the Dentulu legacy system.',
    auth: 'token',
    requestBody: {
      dentulu_office_id: { type: 'string', required: true, example: 'TDMvYi9nPT0' },
      first_name: { type: 'string', required: true, example: 'Sunil' },
      last_name: { type: 'string', required: true, example: 'Kumar' },
      email: { type: 'string', required: true, example: 'sunil.kumar@yopmail.com' },
      mobile: { type: 'string', required: true, example: '2576488565' },
      dob: { type: 'string', required: true, example: '1990-05-15' },
      gender: { type: 'string', required: true, example: 'male' },
      mobile_code: { type: 'string', required: false, example: 'US' },
      address: { type: 'string', required: false, example: '123 Main Street' },
      suite_number: { type: 'string', required: false, example: 'Suite 100' },
      latitude: { type: 'string', required: false, example: '34' },
      longitude: { type: 'string', required: false, example: '-118.243683' },
      city: { type: 'string', required: false, example: 'Los Angeles' },
      state: { type: 'string', required: false, example: 'CA' },
      country: { type: 'string', required: false, example: 'US' },
      pincode: { type: 'string', required: false, example: '90001' },
      street_address: { type: 'string', required: false, example: '123 Main Street' },
      short_address: { type: 'string', required: false, example: '123 Main St, Los Angeles, CA' },
      location_name: { type: 'string', required: false, example: 'Home' }
    },
    responseExample: {
      success: true,
      data: {
        dentulu_patient_id: 'S0h6Vi8wdz0',
        first_name: 'John',
        last_name: 'Deo',
        email: 'john.deo@yopmail.com'
      },
      message: 'Patient created successfully'
    }
  },
  {
    id: 'patient-medical-history',
    category: 'Patient Medical History',
    name: 'Request Patient History',
    method: 'POST',
    path: '/api/developer/patients/medical-history',
    description: 'Request patient medical history from the Dentulu legacy system. Supports batch requests with up to 500 patient IDs.',
    auth: 'token',
    paid: true,
    requestBody: {
      dentulu_patient_ids: { type: 'array', required: true, example: ['SzMvtVMD0', 'SzMvVrWT0'], description: 'List of Dentulu Patient IDs (max 500)' },
      subscriber_id: { type: 'string', required: true, example: 'SzMvVCtrWT0' },
      include_summary: { type: 'boolean', required: false, example: false, description: 'Whether to include summary in response' }
    },
    responseExample: {
      success: true,
      data: {
        status: '1',
        message: 'Data found',
        data: {}
      },
      message: 'Patient medical history retrieved successfully'
    }
  },
  {
    id: 'medical-history-request-batches',
    category: 'Patient Medical History',
    name: 'Get Medical History Request Batches',
    method: 'POST',
    path: '/api/developer/patients/medical-history/request-batches',
    description: 'Get medical history request batches for a subscriber.',
    auth: 'token',
    isFormData: true,
    requestBody: {
      subscriber_id: {
        type: 'string',
        required: true,
        example: ''
      }
    },
    responseExample: {
      success: true,
      data: {},
      message: 'Medical history request batches retrieved successfully'
    }
},
{
  id: 'patient-batch-status',
  category: 'Patient Medical History',
  name: 'Get Patient Batch Status',
  method: 'POST',
  path: '/api/developer/patients/medical-history/batch-status',
  description: 'Get the processing status of a patient medical history batch.',
  auth: 'token',
  isFormData: true,
  requestBody: {
    batch_id: {
      type: 'string',
      required: true,
      example: ''
    },
    subscriber_id: {
      type: 'string',
      required: true,
      example: ''
    }
  },
  responseExample: {
    success: true,
    data: {},
    message: 'Patient batch status retrieved successfully'
  }
},
  {
    id: 'writeback-insurance-benefits',
    category: 'Patient Medical History',
    name: 'Writeback Insurance Benefits',
    method: 'POST',
    path: '/api/developer/patients/medical-history/writeback-insurance-benefits',
    description: 'Upload a patient\'s insurance benefits documents to Dentulu legacy system. Only PDF files are accepted, up to 3 files, 24MB max per file.',
    auth: 'token',
    isFormData: true,
    requestBody: {
      dentulu_patient_id: {
        type: 'string',
        required: true,
        example: 'S0h6Vi8wdz0'
      },
      subscriber_id: { type: 'string', required: true, example: '16118' },
      timeZone: { type: 'string', required: true, example: 'America/New_York' },
      files: {
        type: 'file',
        multiple: true,
        maxFiles: 3,
        required: true,
        accept: 'application/pdf',
        description: 'Insurance benefits documents (PDF only, max 3 files, 24MB max per file)'
      }
    },
    responseExample: {
      success: true,
      data: {
        status: 'success',
        message: 'Insurance documents uploaded successfully'
      },
      message: 'Insurance benefits document(s) uploaded successfully'
    }
  },
  {
    id: 'writeback-physician-notes',
    category: 'Patient Medical History',
    name: 'Writeback Physician Notes',
    method: 'POST',
    path: '/api/developer/patients/medical-history/writeback-physician-notes',
    description: 'Write back a patient\'s physician notes to the Dentulu legacy system.',
    auth: 'token',
    requestBody: {
      dentulu_patient_id: { type: 'string', required: true, example: 'SzNyUjhrWT0' },
      subscriber_id: { type: 'string', required: true, example: 'TDMvYi9nPT0' },
      physician_name: { type: 'string', required: true, example: 'Dr. John Smith' },
      physician_npi: { type: 'string', required: true, example: '1234567893', description: 'NPI number must be 10 digits' },
      notes: { type: 'textarea', required: true, example: 'Patient reviewed. Follow-up appointment recommended.' },
      note_datetime: { type: 'string', required: true, example: '2026-10-28 10:30:00' },
      timeZone: { type: 'string', required: true, example: 'America/New_York' }
    },
    responseExample: {
      success: true,
      data: {
        status: 'success',
        message: 'Physician notes saved successfully'
      },
      message: 'Physician notes written back successfully'
    }
  },
  {
    id: 'patient-insurance-sandbox',
    category: 'Patient Medical History',
    name: 'Get Patient Insurance',
    method: 'POST',
    path: '/api/developer/patients/insurance',
    description: 'Get patient insurance information from the Dentulu legacy system using the sandbox environment.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      dentulu_patient_id: { type: 'string', required: true, example: '17788' },
      dependent_patient_id: { type: 'string', required: false, example: '19277' }
    },
    responseExample: {
      success: true,
      data: {},
      message: 'Patient insurance retrieved successfully'
    }
  },
  {
    id: 'patient-insurance-production',
    category: 'Patient Medical History',
    name: 'Get Patient Insurance',
    method: 'POST',
    path: '/api/developer/patients/insurance',
    description: 'Get patient insurance information from the Dentulu legacy system using the live environment.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      dentulu_patient_id: { type: 'string', required: true, example: '17788' },
      dependent_patient_id: { type: 'string', required: false, example: '19277' }
    },
    responseExample: {
      success: true,
      data: {},
      message: 'Patient insurance retrieved successfully'
    }
  },
  {
    id: 'patient-zus-details',
    category: 'Patient Medical History',
    name: 'Get Patient History Details',
    method: 'POST',
    path: '/api/developer/patients/medical/details',
    description: 'Get detailed patient medical history from the Dentulu legacy system. Uses form-data.',
    auth: 'token',
    isFormData: true,
    requestBody: {
      patient_id: { type: 'string', required: true, example: 'S1h2UjgwZz0' },
      subscriber_id: { type: 'string', required: true, example: 'TDMvYi9nPT0' }
    },
    responseExample: {
      success: true,
      data: {
        status: '1',
        message: 'Data found',
        data: {}
      },
      message: 'Patient medical details retrieved successfully'
    }
  },
  {
    id: 'patient-document-sandbox',
    category: 'Patient Medical History',
    name: 'Get Patient Document',
    method: 'POST',
    path: '/api/developer/patients/document',
    description: 'Get a patient document from the Dentulu legacy system using the sandbox environment.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      binary_url: { type: 'string', required: true, example: 'Binary/9f7b0543-2ad4-4a05-b670-c06eb51d0d41' }
    },
    responseExample: {
      success: true,
      data: {},
      message: 'Patient document retrieved successfully'
    }
  },
  {
    id: 'patient-document-production',
    category: 'Patient Medical History',
    name: 'Get Patient Document',
    method: 'POST',
    path: '/api/developer/patients/document',
    description: 'Get a patient document from the Dentulu legacy system using the live environment.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      binary_url: { type: 'string', required: true, example: 'Binary/9f7b0543-2ad4-4a05-b670-c06eb51d0d41' }
    },
    responseExample: {
      success: true,
      data: {},
      message: 'Patient document retrieved successfully'
    }
  },
  {
    id: 'office-secret-key-sandbox',
    category: 'Practice Management Integration',
    name: 'Get Office Secret Key',
    method: 'POST',
    path: '/api/developer/patients/office-secret-key',
    description: 'Look up the practice management system\'s authorized practice record (including the secret key) for a given office_id, using the sandbox environment.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      office_id: { type: 'string', required: true, example: 'D19408' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        items: [
          { office_id: 'D19408', secretkey: 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' }
        ]
      }
    }
  },
  {
    id: 'request-key-sandbox',
    category: 'Practice Management Integration',
    name: 'Get Request Key',
    method: 'POST',
    path: '/api/developer/patients/request-key',
    description: 'Exchange an office_id and secret_key for a request key, using the sandbox environment. grant_type, app_id and app_key are supplied automatically by the server.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      office_id: { type: 'string', required: true, example: 'D17183' },
      secret_key: { type: 'string', required: true, example: 'B5C714DED1717469SCVG' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        request_key: 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
        expires_in: 3600
      }
    }
  },
  {
    id: 'refresh-key-sandbox',
    category: 'Practice Management Integration',
    name: 'Refresh Request Key',
    method: 'POST',
    path: '/api/developer/patients/refresh-key',
    description: 'Refresh an expired request key using office_id, secret_key and refresh_key, using the sandbox environment. grant_type, app_id and app_key are supplied automatically by the server.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      office_id: { type: 'string', required: true, example: 'D22072' },
      secret_key: { type: 'string', required: true, example: 'A33E3312D2203030FGPV' },
      refresh_key: { type: 'string', required: true, example: 'aa08b2db0cef8d96c7998b25115cfd32' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        request_key: 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
        expires_in: 3600
      }
    }
  },
  {
    id: 'office-secret-key-production',
    category: 'Practice Management Integration',
    name: 'Get Office Secret Key',
    method: 'POST',
    path: '/api/developer/patients/office-secret-key',
    description: 'Look up the practice management system\'s authorized practice record (including the secret key) for a given office_id, using the live environment.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      office_id: { type: 'string', required: true, example: 'D19408' }
    },
    responseExample: {
      success: true,
      data: {
        items: [
          { office_id: 'D19408', secretkey: 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' }
        ]
      }
    }
  },
  {
    id: 'request-key-production',
    category: 'Practice Management Integration',
    name: 'Get Request Key',
    method: 'POST',
    path: '/api/developer/patients/request-key',
    description: 'Exchange an office_id and secret_key for a request key, using the live environment. grant_type, app_id and app_key are supplied automatically by the server.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      office_id: { type: 'string', required: true, example: 'D17183' },
      secret_key: { type: 'string', required: true, example: 'B5C714DED1717469SCVG' }
    },
    responseExample: {
      success: true,
      data: {
        request_key: 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
        expires_in: 3600
      }
    }
  },
  {
    id: 'refresh-key-production',
    category: 'Practice Management Integration',
    name: 'Refresh Request Key',
    method: 'POST',
    path: '/api/developer/patients/refresh-key',
    description: 'Refresh an expired request key using office_id, secret_key and refresh_key, using the live environment. grant_type, app_id and app_key are supplied automatically by the server.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      office_id: { type: 'string', required: true, example: 'D22072' },
      secret_key: { type: 'string', required: true, example: 'A33E3312D2203030FGPV' },
      refresh_key: { type: 'string', required: true, example: 'aa08b2db0cef8d96c7998b25115cfd32' }
    },
    responseExample: {
      success: true,
      data: {
        request_key: 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
        expires_in: 3600
      }
    }
  },
  {
    id: 'practice-list-sandbox',
    category: 'Practice Management Integration',
    name: 'Get Practice List',
    method: 'POST',
    path: '/api/developer/practice/practices',
    description: 'Fetch a paginated list of practices from the practice management system using a request_key, using the sandbox environment.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        items: [
          { practice_id: '1', practice_name: 'Main Office' }
        ],
        total_count: '1'
      }
    }
  },
  {
    id: 'practice-list-production',
    category: 'Practice Management Integration',
    name: 'Get Practice List',
    method: 'POST',
    path: '/api/developer/practice/practices',
    description: 'Fetch a paginated list of practices from the practice management system using a request_key, using the live environment.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' }
    },
    responseExample: {
      success: true,
      data: {
        items: [
          { practice_id: '1', practice_name: 'Main Office' }
        ],
        total_count: '1'
      }
    }
  },
  {
    id: 'providers-list-sandbox',
    category: 'Practice Management Integration',
    name: 'Get Providers List',
    method: 'POST',
    path: '/api/developer/practice/providers',
    description: 'Fetch a paginated list of providers from the practice management system using a request_key, using the sandbox environment.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' },
      status: { type: 'select', required: true, options: ['active', 'inactive'], example: 'active' },
      provider_type: { type: 'string', required: false, example: '' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        items: [
          { provider_id: '1', firstname: 'John', lastname: 'Smith', status: 'active' }
        ],
        total_count: '1'
      }
    }
  },
  {
    id: 'providers-list-production',
    category: 'Practice Management Integration',
    name: 'Get Providers List',
    method: 'POST',
    path: '/api/developer/practice/providers',
    description: 'Fetch a paginated list of providers from the practice management system using a request_key, using the live environment.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' },
      status: { type: 'select', required: true, options: ['active', 'inactive'], example: 'active' },
      provider_type: { type: 'string', required: false, example: '' }
    },
    responseExample: {
      success: true,
      data: {
        items: [
          { provider_id: '1', firstname: 'John', lastname: 'Smith', status: 'active' }
        ],
        total_count: '1'
      }
    }
  },
  {
    id: 'operatories-sikka-sandbox',
    category: 'Practice Management Integration',
    name: 'Get Operatories',
    method: 'POST',
    path: '/api/developer/practice/operatories',
    description: 'Fetch a paginated list of operatories from the practice management system using a request_key, using the sandbox environment.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' },
      cust_id: { type: 'string', required: false, example: '' },
      is_hidden: { type: 'select', required: false, options: ['T', 'F'], example: 'F' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        items: [
          { operatory_id: '1', name: 'Op 1', cust_id: '1', is_hidden: 'F' }
        ],
        total_count: '4'
      }
    }
  },
  {
    id: 'operatories-sikka-production',
    category: 'Practice Management Integration',
    name: 'Get Operatories',
    method: 'POST',
    path: '/api/developer/practice/operatories',
    description: 'Fetch a paginated list of operatories from the practice management system using a request_key, using the live environment.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' },
      cust_id: { type: 'string', required: false, example: '' },
      is_hidden: { type: 'select', required: false, options: ['T', 'F'], example: 'F' }
    },
    responseExample: {
      success: true,
      data: {
        items: [
          { operatory_id: '1', name: 'Op 1', cust_id: '1', is_hidden: 'F' }
        ],
        total_count: '4'
      }
    }
  },
  {
    id: 'pms-users-sikka-sandbox',
    category: 'Practice Management Integration',
    name: 'Get PMS Users',
    method: 'POST',
    path: '/api/developer/practice/pms-users',
    description: 'Fetch a paginated list of practice management system users using a request_key, using the sandbox environment.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' }
    },
    responseExample: {
      success: true,
      data: {
        items: [
          { pms_user_id: '1', practice_id: '1', cust_id: '1', username: 'jsmith' }
        ],
        total_count: '1'
      }
    }
  },
  {
    id: 'pms-users-sikka-production',
    category: 'Practice Management Integration',
    name: 'Get PMS Users',
    method: 'POST',
    path: '/api/developer/practice/pms-users',
    description: 'Fetch a paginated list of practice management system users using a request_key, using the live environment.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' }
    },
    responseExample: {
      success: true,
      data: {
        items: [
          { pms_user_id: '1', practice_id: '1', cust_id: '1', username: 'jsmith' }
        ],
        total_count: '1'
      }
    }
  },
  {
    id: 'appointments-list-sikka-sandbox',
    category: 'Practice Management Integration',
    group: 'Appointments',
    name: 'Get Appointments',
    method: 'POST',
    path: '/api/developer/practice/appointments',
    description: 'Fetch a paginated list of appointments from the practice management system using a request_key, using the sandbox environment.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' },
      patient_id: { type: 'string', required: false, example: '35' },
      provider_id: { type: 'string', required: false, example: 'DOC' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        items: [
          { appointment_id: '101', patient_id: '35', provider_id: 'DOC', date: '2026-07-18', status: 'confirmed' }
        ],
        total_count: '1'
      }
    }
  },
  {
    id: 'appointments-list-sikka-production',
    category: 'Practice Management Integration',
    group: 'Appointments',
    name: 'Get Appointments',
    method: 'POST',
    path: '/api/developer/practice/appointments',
    description: 'Fetch a paginated list of appointments from the practice management system using a request_key, using the live environment.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' },
      patient_id: { type: 'string', required: false, example: '35' },
      provider_id: { type: 'string', required: false, example: 'DOC' }
    },
    responseExample: {
      success: true,
      data: {
        items: [
          { appointment_id: '101', patient_id: '35', provider_id: 'DOC', date: '2026-07-18', status: 'confirmed' }
        ],
        total_count: '1'
      }
    }
  },
  {
    id: 'create-appointment-sikka-sandbox',
    category: 'Practice Management Integration',
    group: 'Appointments',
    name: 'Create Appointment',
    method: 'POST',
    path: '/api/developer/practice/appointment',
    description: 'Create a new appointment in the practice management system using a request_key, using the sandbox environment. request_key, date, time, provider_id and practice_id are always required; the rest vary by PMS.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      patient_id: { type: 'string', required: false, example: '4100' },
      date: { type: 'string', required: true, example: '2026-07-27', placeholder: 'YYYY-MM-DD' },
      description: { type: 'string', required: false, example: 'appointment description' },
      time: { type: 'string', required: true, example: '13:30' },
      provider_id: { type: 'string', required: true, example: 'DT04' },
      length: { type: 'string', required: false, example: '20' },
      operatory: { type: 'string', required: false, example: 'OP03' },
      practice_id: { type: 'string', required: true, example: '1' },
      type: { type: 'string', required: false, example: 'TestType' },
      user: { type: 'string', required: false, example: '304234244' },
      status: { type: 'string', required: false, example: '123' },
      note: { type: 'string', required: false, example: 'Note text' },
      procedure_code: { type: 'string', required: false, example: 'D2510' },
      amount: { type: 'string', required: false, example: '' },
      tooth: { type: 'string', required: false, example: '1' },
      surface: { type: 'string', required: false, example: 'D' },
      root: { type: 'string', required: false, example: '' },
      quadrant: { type: 'string', required: false, example: '' },
      is_guarantor_exist: { type: 'string', required: false, example: '' },
      gender: { type: 'string', required: false, example: '' },
      workphone: { type: 'string', required: false, example: '' },
      cell: { type: 'string', required: false, example: '' },
      other_phone: { type: 'string', required: false, example: '' },
      address_line1: { type: 'string', required: false, example: '' },
      address_line2: { type: 'string', required: false, example: '' },
      city: { type: 'string', required: false, example: '' },
      state: { type: 'string', required: false, example: '' },
      zipcode: { type: 'string', required: false, example: '' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        appointment_id: '101',
        patient_id: '4100',
        provider_id: 'DT04',
        date: '2026-07-27',
        time: '13:30',
        status: '123'
      }
    }
  },
  {
    id: 'create-appointment-sikka-production',
    category: 'Practice Management Integration',
    group: 'Appointments',
    name: 'Create Appointment',
    method: 'POST',
    path: '/api/developer/practice/appointment',
    description: 'Create a new appointment in the practice management system using a request_key, using the live environment. request_key, date, time, provider_id and practice_id are always required; the rest vary by PMS.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      patient_id: { type: 'string', required: false, example: '4100' },
      date: { type: 'string', required: true, example: '2026-07-27', placeholder: 'YYYY-MM-DD' },
      description: { type: 'string', required: false, example: 'appointment description' },
      time: { type: 'string', required: true, example: '13:30' },
      provider_id: { type: 'string', required: true, example: 'DT04' },
      length: { type: 'string', required: false, example: '20' },
      operatory: { type: 'string', required: false, example: 'OP03' },
      practice_id: { type: 'string', required: true, example: '1' },
      type: { type: 'string', required: false, example: 'TestType' },
      user: { type: 'string', required: false, example: '304234244' },
      status: { type: 'string', required: false, example: '123' },
      note: { type: 'string', required: false, example: 'Note text' },
      procedure_code: { type: 'string', required: false, example: 'D2510' },
      amount: { type: 'string', required: false, example: '' },
      tooth: { type: 'string', required: false, example: '1' },
      surface: { type: 'string', required: false, example: 'D' },
      root: { type: 'string', required: false, example: '' },
      quadrant: { type: 'string', required: false, example: '' },
      is_guarantor_exist: { type: 'string', required: false, example: '' },
      gender: { type: 'string', required: false, example: '' },
      workphone: { type: 'string', required: false, example: '' },
      cell: { type: 'string', required: false, example: '' },
      other_phone: { type: 'string', required: false, example: '' },
      address_line1: { type: 'string', required: false, example: '' },
      address_line2: { type: 'string', required: false, example: '' },
      city: { type: 'string', required: false, example: '' },
      state: { type: 'string', required: false, example: '' },
      zipcode: { type: 'string', required: false, example: '' }
    },
    responseExample: {
      success: true,
      data: {
        appointment_id: '101',
        patient_id: '4100',
        provider_id: 'DT04',
        date: '2026-07-27',
        time: '13:30',
        status: '123'
      }
    }
  },
  {
    id: 'update-appointment-sikka-sandbox',
    category: 'Practice Management Integration',
    group: 'Appointments',
    name: 'Update Appointment',
    method: 'PUT',
    path: '/api/developer/practice/appointment/{appointment_sr_no}',
    description: 'Update an existing appointment in the practice management system using a request_key, using the sandbox environment. appointment_sr_no, request_key, practice_id and provider_id are always required; all other fields are optional.',
    auth: 'token',
    environment: 'sandbox',
    pathParams: {
      appointment_sr_no: { type: 'string', required: true, example: '101' }
    },
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      practice_id: { type: 'string', required: true, example: '1' },
      date: { type: 'string', required: false, example: '2025-10-31', placeholder: 'YYYY-MM-DD' },
      time: { type: 'string', required: false, example: '10:40', placeholder: 'HH:MM' },
      length: { type: 'string', required: false, example: '20' },
      operatory: { type: 'string', required: false, example: '1234' },
      provider_id: { type: 'string', required: true, example: 'Test1' },
      status: { type: 'string', required: false, example: 'READY' },
      description: { type: 'string', required: false, example: 'Update broken appt' },
      schedule: { type: 'string', required: false, example: '' },
      staff: { type: 'string', required: false, example: '' },
      type: { type: 'string', required: false, example: '' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        appointment_id: '101'
      }
    }
  },
  {
    id: 'update-appointment-sikka-production',
    category: 'Practice Management Integration',
    group: 'Appointments',
    name: 'Update Appointment',
    method: 'PUT',
    path: '/api/developer/practice/appointment/{appointment_sr_no}',
    description: 'Update an existing appointment in the practice management system using a request_key, using the live environment. appointment_sr_no, request_key, practice_id and provider_id are always required; all other fields are optional.',
    auth: 'token',
    environment: 'production',
    pathParams: {
      appointment_sr_no: { type: 'string', required: true, example: '101' }
    },
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      practice_id: { type: 'string', required: true, example: '1' },
      date: { type: 'string', required: false, example: '2025-10-31', placeholder: 'YYYY-MM-DD' },
      time: { type: 'string', required: false, example: '10:40', placeholder: 'HH:MM' },
      length: { type: 'string', required: false, example: '20' },
      operatory: { type: 'string', required: false, example: '1234' },
      provider_id: { type: 'string', required: true, example: 'Test1' },
      status: { type: 'string', required: false, example: 'READY' },
      description: { type: 'string', required: false, example: 'Update broken appt' },
      schedule: { type: 'string', required: false, example: '' },
      staff: { type: 'string', required: false, example: '' },
      type: { type: 'string', required: false, example: '' }
    },
    responseExample: {
      success: true,
      data: {
        appointment_id: '101'
      }
    }
  },
  {
    id: 'appointment-statuses-sikka-sandbox',
    category: 'Practice Management Integration',
    group: 'Appointments',
    name: 'Get Appointment Statuses',
    method: 'POST',
    path: '/api/developer/practice/appointment-statuses',
    description: 'Fetch the list of appointment statuses configured in the practice management system using a request_key, using the sandbox environment.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      practice_id: { type: 'string', required: false, example: '1' },
      status: { type: 'string', required: false, example: '' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        items: [
          { status: 'Complete', practice_id: '1', cust_id: '1' },
          { status: 'Scheduled', practice_id: '1', cust_id: '1' }
        ],
        total_count: '2'
      }
    }
  },
  {
    id: 'appointment-statuses-sikka-production',
    category: 'Practice Management Integration',
    group: 'Appointments',
    name: 'Get Appointment Statuses',
    method: 'POST',
    path: '/api/developer/practice/appointment-statuses',
    description: 'Fetch the list of appointment statuses configured in the practice management system using a request_key, using the live environment.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      practice_id: { type: 'string', required: false, example: '1' },
      status: { type: 'string', required: false, example: '' }
    },
    responseExample: {
      success: true,
      data: {
        items: [
          { status: 'Complete', practice_id: '1', cust_id: '1' },
          { status: 'Scheduled', practice_id: '1', cust_id: '1' }
        ],
        total_count: '2'
      }
    }
  },
  {
    id: 'patient-list-sikka-sandbox',
    category: 'Practice Management Integration',
    group: 'Patients',
    name: 'Get Patient List',
    method: 'POST',
    path: '/api/developer/practice/patients',
    description: 'Fetch a paginated list of patients from the practice management system using a request_key, using the sandbox environment.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        items: [
          { patient_id: '34', firstname: 'James', lastname: 'Jones', status: 'patient' }
        ],
        total_count: '38'
      }
    }
  },
  {
    id: 'patient-list-sikka-production',
    category: 'Practice Management Integration',
    group: 'Patients',
    name: 'Get Patient List',
    method: 'POST',
    path: '/api/developer/practice/patients',
    description: 'Fetch a paginated list of patients from the practice management system using a request_key, using the live environment.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' }
    },
    responseExample: {
      success: true,
      data: {
        items: [
          { patient_id: '34', firstname: 'James', lastname: 'Jones', status: 'patient' }
        ],
        total_count: '38'
      }
    }
  },
  {
    id: 'create-patient-sikka-sandbox',
    category: 'Practice Management Integration',
    group: 'Patients',
    name: 'Create Patient',
    method: 'POST',
    path: '/api/developer/practice/patient',
    description: 'Create a new patient in the practice management system using a request_key, using the sandbox environment. Use the returned id with Get Create Patient Status to check the writeback status.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      firstname: { type: 'string', required: true, example: 'Test' },
      lastname: { type: 'string', required: true, example: 'Last' },
      practice_id: { type: 'string', required: true, example: '1-1' },
      birthdate: { type: 'string', required: true, example: '1999-12-20' },
      provider_id: { type: 'string', required: true, example: 'DOC' },
      gender: { type: 'string', required: true, example: 'male' },
      is_guarantor_exist: { type: 'string', required: false, example: 'false' },
      guarantor_id: { type: 'string', required: false, example: '' },
      email: { type: 'string', required: false, example: 'dinesh.babuXXXX@gmail.com' },
      workphone: { type: 'string', required: false, example: '1243544343' },
      homephone: { type: 'string', required: false, example: '1243544343' },
      cell: { type: 'string', required: false, example: '1243544343' },
      other_phone: { type: 'string', required: false, example: '1243544343' },
      staff: { type: 'string', required: false, example: '1' },
      status: { type: 'string', required: false, example: '11' },
      type: { type: 'string', required: false, example: '5' },
      primary_relationship: { type: 'string', required: false, example: '5' },
      user: { type: 'string', required: false, example: '1' },
      treatment: { type: 'string', required: false, example: '15' },
      middlename: { type: 'string', required: false, example: '' },
      preferred_name: { type: 'string', required: false, example: '' },
      ssn: { type: 'string', required: false, example: '' },
      address_line1: { type: 'string', required: false, example: 'address_line1' },
      address_line2: { type: 'string', required: false, example: 'address_line2' },
      city: { type: 'string', required: false, example: 'city' },
      state: { type: 'string', required: false, example: 'state' },
      zipcode: { type: 'string', required: false, example: 'zipcode' },
      country: { type: 'string', required: false, example: 'country' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        patient_id: '35'
      }
    }
  },
  {
    id: 'create-patient-sikka-production',
    category: 'Practice Management Integration',
    group: 'Patients',
    name: 'Create Patient',
    method: 'POST',
    path: '/api/developer/practice/patient',
    description: 'Create a new patient in the practice management system using a request_key, using the live environment. Use the returned id with Get Create Patient Status to check the writeback status.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      firstname: { type: 'string', required: true, example: 'Test' },
      lastname: { type: 'string', required: true, example: 'Last' },
      practice_id: { type: 'string', required: true, example: '1-1' },
      birthdate: { type: 'string', required: true, example: '1999-12-20' },
      provider_id: { type: 'string', required: true, example: 'DOC' },
      gender: { type: 'string', required: true, example: 'male' },
      is_guarantor_exist: { type: 'string', required: false, example: 'false' },
      guarantor_id: { type: 'string', required: false, example: '' },
      email: { type: 'string', required: false, example: 'dinesh.babuXXXX@gmail.com' },
      workphone: { type: 'string', required: false, example: '1243544343' },
      homephone: { type: 'string', required: false, example: '1243544343' },
      cell: { type: 'string', required: false, example: '1243544343' },
      other_phone: { type: 'string', required: false, example: '1243544343' },
      staff: { type: 'string', required: false, example: '1' },
      status: { type: 'string', required: false, example: '11' },
      type: { type: 'string', required: false, example: '5' },
      primary_relationship: { type: 'string', required: false, example: '5' },
      user: { type: 'string', required: false, example: '1' },
      treatment: { type: 'string', required: false, example: '15' },
      middlename: { type: 'string', required: false, example: '' },
      preferred_name: { type: 'string', required: false, example: '' },
      ssn: { type: 'string', required: false, example: '' },
      address_line1: { type: 'string', required: false, example: 'address_line1' },
      address_line2: { type: 'string', required: false, example: 'address_line2' },
      city: { type: 'string', required: false, example: 'city' },
      state: { type: 'string', required: false, example: 'state' },
      zipcode: { type: 'string', required: false, example: 'zipcode' },
      country: { type: 'string', required: false, example: 'country' }
    },
    responseExample: {
      success: true,
      data: {
        patient_id: '35'
      }
    }
  },
  {
    id: 'get-patient-status-sikka-sandbox',
    category: 'Practice Management Integration',
    name: 'Get Writeback Status',
    method: 'POST',
    path: '/api/developer/practice/patient/status',
    description: 'Check the writeback status of a create-patient request using the id returned by Create Patient, using the sandbox environment.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      id: { type: 'string', required: true, example: '4525120' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        items: [
          { id: '4525120', status: 'success' }
        ],
        total_count: '1'
      }
    }
  },
  {
    id: 'get-patient-status-sikka-production',
    category: 'Practice Management Integration',
    name: 'Get Writeback Status',
    method: 'POST',
    path: '/api/developer/practice/patient/status',
    description: 'Check the writeback status of a create-patient request using the id returned by Create Patient, using the live environment.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      id: { type: 'string', required: true, example: '4525120' }
    },
    responseExample: {
      success: true,
      data: {
        items: [
          { id: '4525120', status: 'success' }
        ],
        total_count: '1'
      }
    }
  },
  {
    id: 'writeback-details-sikka-sandbox',
    category: 'Practice Management Integration',
    name: 'Get Writeback Details',
    method: 'POST',
    path: '/api/developer/practice/writeback-details',
    description: 'Fetch a paginated list of writeback details from the practice management system, using the sandbox environment.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        items: [
          { id: '4525120', status: 'success', message: '' }
        ],
        total_count: '1'
      }
    }
  },
  {
    id: 'writeback-details-sikka-production',
    category: 'Practice Management Integration',
    name: 'Get Writeback Details',
    method: 'POST',
    path: '/api/developer/practice/writeback-details',
    description: 'Fetch a paginated list of writeback details from the practice management system, using the live environment.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' }
    },
    responseExample: {
      success: true,
      data: {
        items: [
          { id: '4525120', status: 'success', message: '' }
        ],
        total_count: '1'
      }
    }
  },
  {
    id: 'update-patient-sikka-sandbox',
    category: 'Practice Management Integration',
    group: 'Patients',
    name: 'Update Patient',
    method: 'PATCH',
    path: '/api/developer/practice/patient/{patient_id}',
    description: 'Update an existing patient in the practice management system using a request_key, using the sandbox environment. Only the fields you supply are updated.',
    auth: 'token',
    environment: 'sandbox',
    pathParams: {
      patient_id: { type: 'string', required: true, example: '35' }
    },
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      practice_id: { type: 'string', required: true, example: '1-1' },
      firstname: { type: 'string', required: false, example: '' },
      middlename: { type: 'string', required: false, example: '' },
      lastname: { type: 'string', required: false, example: '' },
      preferred_name: { type: 'string', required: false, example: '' },
      formal: { type: 'string', required: false, example: '' },
      informal: { type: 'string', required: false, example: '' },
      birthdate: { type: 'string', required: false, example: '1999-12-20' },
      ssn: { type: 'string', required: false, example: '' },
      model: { type: 'string', required: false, example: '' },
      gender: { type: 'string', required: false, example: 'male' },
      salutation: { type: 'string', required: false, example: '' },
      status: { type: 'string', required: false, example: '' },
      type: { type: 'string', required: false, example: '' },
      race: { type: 'string', required: false, example: '' },
      provider_id: { type: 'string', required: false, example: 'DOC' },
      staff: { type: 'string', required: false, example: '' },
      language: { type: 'string', required: false, example: '' },
      recall_date: { type: 'string', required: false, example: '' },
      mail_method: { type: 'string', required: false, example: '' },
      gender_identifier: { type: 'string', required: false, example: '' },
      school: { type: 'string', required: false, example: '' },
      email: { type: 'string', required: false, example: '' },
      workphone: { type: 'string', required: false, example: '' },
      phone: { type: 'string', required: false, example: '' },
      cell: { type: 'string', required: false, example: '' },
      address_line1: { type: 'string', required: false, example: '' },
      address_line2: { type: 'string', required: false, example: '' },
      city: { type: 'string', required: false, example: '' },
      state: { type: 'string', required: false, example: '' },
      zipcode: { type: 'string', required: false, example: '' },
      country: { type: 'string', required: false, example: '' },
      email_extension: { type: 'string', required: false, example: '' },
      workphone_extension: { type: 'string', required: false, example: '' },
      phone_extension: { type: 'string', required: false, example: '' },
      cell_extension: { type: 'string', required: false, example: '' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        patient_id: '35'
      }
    }
  },
  {
    id: 'update-patient-sikka-production',
    category: 'Practice Management Integration',
    group: 'Patients',
    name: 'Update Patient',
    method: 'PATCH',
    path: '/api/developer/practice/patient/{patient_id}',
    description: 'Update an existing patient in the practice management system using a request_key, using the live environment. Only the fields you supply are updated.',
    auth: 'token',
    environment: 'production',
    pathParams: {
      patient_id: { type: 'string', required: true, example: '35' }
    },
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      practice_id: { type: 'string', required: true, example: '1-1' },
      firstname: { type: 'string', required: false, example: '' },
      middlename: { type: 'string', required: false, example: '' },
      lastname: { type: 'string', required: false, example: '' },
      preferred_name: { type: 'string', required: false, example: '' },
      formal: { type: 'string', required: false, example: '' },
      informal: { type: 'string', required: false, example: '' },
      birthdate: { type: 'string', required: false, example: '1999-12-20' },
      ssn: { type: 'string', required: false, example: '' },
      model: { type: 'string', required: false, example: '' },
      gender: { type: 'string', required: false, example: 'male' },
      salutation: { type: 'string', required: false, example: '' },
      status: { type: 'string', required: false, example: '' },
      type: { type: 'string', required: false, example: '' },
      race: { type: 'string', required: false, example: '' },
      provider_id: { type: 'string', required: false, example: 'DOC' },
      staff: { type: 'string', required: false, example: '' },
      language: { type: 'string', required: false, example: '' },
      recall_date: { type: 'string', required: false, example: '' },
      mail_method: { type: 'string', required: false, example: '' },
      gender_identifier: { type: 'string', required: false, example: '' },
      school: { type: 'string', required: false, example: '' },
      email: { type: 'string', required: false, example: '' },
      workphone: { type: 'string', required: false, example: '' },
      phone: { type: 'string', required: false, example: '' },
      cell: { type: 'string', required: false, example: '' },
      address_line1: { type: 'string', required: false, example: '' },
      address_line2: { type: 'string', required: false, example: '' },
      city: { type: 'string', required: false, example: '' },
      state: { type: 'string', required: false, example: '' },
      zipcode: { type: 'string', required: false, example: '' },
      country: { type: 'string', required: false, example: '' },
      email_extension: { type: 'string', required: false, example: '' },
      workphone_extension: { type: 'string', required: false, example: '' },
      phone_extension: { type: 'string', required: false, example: '' },
      cell_extension: { type: 'string', required: false, example: '' }
    },
    responseExample: {
      success: true,
      data: {
        patient_id: '35'
      }
    }
  },
  {
    id: 'perio-charts-sikka-sandbox',
    category: 'Practice Management Integration',
    group: 'Clinical/Tooth Chart Findings',
    name: 'Get Perio Chart Findings',
    method: 'POST',
    path: '/api/developer/practice/perio-charts',
    description: 'Fetch a paginated list of periodontal (tooth) chart findings from the practice management system using a request_key, using the sandbox environment.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' },
      patient_id: { type: 'string', required: false, example: '34' },
      practice_id: { type: 'string', required: false, example: '1-1' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        items: [
          { patient_id: '34', practice_id: '1-1', tooth: '3', quadrant: 'UR', pocket_depth: '3', gingival_margin: '0', bleeding: 'false', plaque: 'false', date: '2024-01-15' }
        ],
        total_count: '32'
      }
    }
  },
  {
    id: 'perio-charts-sikka-production',
    category: 'Practice Management Integration',
    group: 'Clinical/Tooth Chart Findings',
    name: 'Get Perio Chart Findings',
    method: 'POST',
    path: '/api/developer/practice/perio-charts',
    description: 'Fetch a paginated list of periodontal (tooth) chart findings from the practice management system using a request_key, using the live environment.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' },
      patient_id: { type: 'string', required: false, example: '34' },
      practice_id: { type: 'string', required: false, example: '1-1' }
    },
    responseExample: {
      success: true,
      data: {
        items: [
          { patient_id: '34', practice_id: '1-1', tooth: '3', quadrant: 'UR', pocket_depth: '3', gingival_margin: '0', bleeding: 'false', plaque: 'false', date: '2024-01-15' }
        ],
        total_count: '32'
      }
    }
  },
  {
    id: 'create-perio-chart-sikka-sandbox',
    category: 'Practice Management Integration',
    group: 'Clinical/Tooth Chart Findings',
    name: 'Create Perio Chart Finding',
    method: 'POST',
    path: '/api/developer/practice/perio-chart',
    description: 'Create a new periodontal (tooth) chart finding in the practice management system using a request_key, using the sandbox environment.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      patient_id: { type: 'string', required: true, example: '17198' },
      practice_id: { type: 'string', required: true, example: '2' },
      provider_id: { type: 'string', required: true, example: 'XXX1' },
      tooth_number: { type: 'string', required: true, example: '2' },
      chart_date: { type: 'string', required: true, example: '2025-10-26' },
      script: { type: 'string', required: false, example: 'Dentrix Clinical Attachment Level' },
      pocket_depth: { type: 'string', required: false, example: '1|2|3|4|5|6' },
      gingival_margin: { type: 'string', required: false, example: '1|2|3|4|5|6' },
      mucogingival_junction: { type: 'string', required: false, example: '1|2|3|4|5|6' },
      bleed: { type: 'string', required: false, example: '1|||1||' },
      suppuration: { type: 'string', required: false, example: '||1|||1' },
      calculus: { type: 'string', required: false, example: '5|5|5|5|5|5' },
      furcation: { type: 'string', required: false, example: '|F2:Penetrates into furcation||F1:Probe root indentation||F1:Probe root indentation' },
      mobility: { type: 'string', required: false, example: '1' },
      plaque: { type: 'string', required: false, example: 'Moderate' },
      bone_loss: { type: 'string', required: false, example: 'Moderate' },
      check_spu: { type: 'string', required: false, example: '' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        patient_id: '17198',
        tooth_number: '2',
        chart_date: '2025-10-26'
      }
    }
  },
  {
    id: 'create-perio-chart-sikka-production',
    category: 'Practice Management Integration',
    group: 'Clinical/Tooth Chart Findings',
    name: 'Create Perio Chart Finding',
    method: 'POST',
    path: '/api/developer/practice/perio-chart',
    description: 'Create a new periodontal (tooth) chart finding in the practice management system using a request_key, using the live environment.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      patient_id: { type: 'string', required: true, example: '17198' },
      practice_id: { type: 'string', required: true, example: '2' },
      provider_id: { type: 'string', required: true, example: 'XXX1' },
      tooth_number: { type: 'string', required: true, example: '2' },
      chart_date: { type: 'string', required: true, example: '2025-10-26' },
      script: { type: 'string', required: false, example: 'Dentrix Clinical Attachment Level' },
      pocket_depth: { type: 'string', required: false, example: '1|2|3|4|5|6' },
      gingival_margin: { type: 'string', required: false, example: '1|2|3|4|5|6' },
      mucogingival_junction: { type: 'string', required: false, example: '1|2|3|4|5|6' },
      bleed: { type: 'string', required: false, example: '1|||1||' },
      suppuration: { type: 'string', required: false, example: '||1|||1' },
      calculus: { type: 'string', required: false, example: '5|5|5|5|5|5' },
      furcation: { type: 'string', required: false, example: '|F2:Penetrates into furcation||F1:Probe root indentation||F1:Probe root indentation' },
      mobility: { type: 'string', required: false, example: '1' },
      plaque: { type: 'string', required: false, example: 'Moderate' },
      bone_loss: { type: 'string', required: false, example: 'Moderate' },
      check_spu: { type: 'string', required: false, example: '' }
    },
    responseExample: {
      success: true,
      data: {
        patient_id: '17198',
        tooth_number: '2',
        chart_date: '2025-10-26'
      }
    }
  },
  {
    id: 'procedure-codes-sikka-sandbox',
    category: 'Practice Management Integration',
    group: 'Procedure and CDT Codes',
    name: 'Procedure Codes',
    method: 'POST',
    path: '/api/developer/practice/procedure-codes',
    description: 'Fetch a paginated list of procedure/CDT codes from the practice management system using a request_key, using the sandbox environment.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        items: [
          { procedure_code: 'D0120', description: 'Periodic oral evaluation - established patient', category: 'Diagnostic' }
        ],
        total_count: '412'
      }
    }
  },
  {
    id: 'procedure-codes-sikka-production',
    category: 'Practice Management Integration',
    group: 'Procedure and CDT Codes',
    name: 'Procedure Codes',
    method: 'POST',
    path: '/api/developer/practice/procedure-codes',
    description: 'Fetch a paginated list of procedure/CDT codes from the practice management system using a request_key, using the live environment.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' }
    },
    responseExample: {
      success: true,
      data: {
        items: [
          { procedure_code: 'D0120', description: 'Periodic oral evaluation - established patient', category: 'Diagnostic' }
        ],
        total_count: '412'
      }
    }
  },
  {
    id: 'create-procedure-sikka-sandbox',
    category: 'Practice Management Integration',
    group: 'Procedure and CDT Codes',
    name: 'Create Procedure',
    method: 'POST',
    path: '/api/developer/practice/procedure',
    description: 'Create a new procedure in the practice management system using a request_key, using the sandbox environment. procedure_code_number is required only for OpenDental.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      patient_id: { type: 'string', required: true, example: '17198' },
      procedure_date: { type: 'string', required: true, example: '2025-10-26' },
      procedure_entry_date: { type: 'string', required: true, example: '2025-10-26' },
      practice_id: { type: 'string', required: true, example: '2' },
      procedure_code: { type: 'string', required: true, example: 'D0120' },
      procedure_description: { type: 'string', required: true, example: 'Periodic oral evaluation' },
      amount: { type: 'string', required: true, example: '100' },
      procedure_code_number: { type: 'string', required: false, example: '', description: 'Required only for OpenDental' },
      provider_id: { type: 'string', required: false, example: 'XXX1' },
      guarantor_id: { type: 'string', required: false, example: '' },
      tooth: { type: 'string', required: false, example: '3' },
      surface: { type: 'string', required: false, example: '' },
      root: { type: 'string', required: false, example: '' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        patient_id: '17198',
        procedure_code: 'D0120',
        procedure_date: '2025-10-26'
      }
    }
  },
  {
    id: 'create-procedure-sikka-production',
    category: 'Practice Management Integration',
    group: 'Procedure and CDT Codes',
    name: 'Create Procedure',
    method: 'POST',
    path: '/api/developer/practice/procedure',
    description: 'Create a new procedure in the practice management system using a request_key, using the live environment. procedure_code_number is required only for OpenDental.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      patient_id: { type: 'string', required: true, example: '17198' },
      procedure_date: { type: 'string', required: true, example: '2025-10-26' },
      procedure_entry_date: { type: 'string', required: true, example: '2025-10-26' },
      practice_id: { type: 'string', required: true, example: '2' },
      procedure_code: { type: 'string', required: true, example: 'D0120' },
      procedure_description: { type: 'string', required: true, example: 'Periodic oral evaluation' },
      amount: { type: 'string', required: true, example: '100' },
      procedure_code_number: { type: 'string', required: false, example: '', description: 'Required only for OpenDental' },
      provider_id: { type: 'string', required: false, example: 'XXX1' },
      guarantor_id: { type: 'string', required: false, example: '' },
      tooth: { type: 'string', required: false, example: '3' },
      surface: { type: 'string', required: false, example: '' },
      root: { type: 'string', required: false, example: '' }
    },
    responseExample: {
      success: true,
      data: {
        patient_id: '17198',
        procedure_code: 'D0120',
        procedure_date: '2025-10-26'
      }
    }
  },
  {
    id: 'treatment-plans-sikka-sandbox',
    category: 'Practice Management Integration',
    group: 'Treatment Plans',
    name: 'Get Treatment Plans',
    method: 'POST',
    path: '/api/developer/practice/treatment-plans',
    description: 'Fetch a paginated list of treatment plans from the practice management system using a request_key, using the sandbox environment.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        items: [
          { patient_id: '34', practice_id: '1-1', treatment_plan_id: '210', status: 'active', date: '2024-01-15' }
        ],
        total_count: '12'
      }
    }
  },
  {
    id: 'treatment-plans-sikka-production',
    category: 'Practice Management Integration',
    group: 'Treatment Plans',
    name: 'Get Treatment Plans',
    method: 'POST',
    path: '/api/developer/practice/treatment-plans',
    description: 'Fetch a paginated list of treatment plans from the practice management system using a request_key, using the live environment.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' }
    },
    responseExample: {
      success: true,
      data: {
        items: [
          { patient_id: '34', practice_id: '1-1', treatment_plan_id: '210', status: 'active', date: '2024-01-15' }
        ],
        total_count: '12'
      }
    }
  },
  {
    id: 'create-treatment-plan-sikka-sandbox',
    category: 'Practice Management Integration',
    group: 'Treatment Plans',
    name: 'Create Treatment Plan',
    method: 'POST',
    path: '/api/developer/practice/treatment-plan',
    description: 'Create a new treatment plan in the practice management system using a request_key, using the sandbox environment.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      patient_id: { type: 'string', required: true, example: '18' },
      practice_id: { type: 'string', required: true, example: '1' },
      treatment_plan_name: { type: 'string', required: true, example: 'New Treatmentplan for all Treatment', maxLength: 40 },
      procedure_code: { type: 'string', required: true, example: 'D2510' },
      user: { type: 'string', required: true, example: '02' },
      amount: { type: 'string', required: false, example: '' },
      tooth: { type: 'string', required: false, example: '1' },
      surface: { type: 'string', required: false, example: 'D' },
      root: { type: 'string', required: false, example: '' },
      provider_id: { type: 'string', required: false, example: '' },
      entry_date: { type: 'string', required: false, example: '' },
      procedure_date: { type: 'string', required: false, example: '' },
      note: { type: 'string', required: false, example: '', maxLength: 250 },
      guarantor_id: { type: 'string', required: false, example: '' },
      attach_to_appointment: { type: 'string', required: false, example: 'false' },
      appointment_sr_no: { type: 'string', required: false, example: '857' },
      plan_sr_no: { type: 'string', required: false, example: '210648' },
      treatment_plan_heading: { type: 'string', required: false, example: '' },
      treatment_plan_type: { type: 'string', required: false, example: 'insurance' },
      priority: { type: 'string', required: false, example: '' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        patient_id: '18',
        plan_sr_no: '210648',
        treatment_plan_name: 'New Treatmentplan for all Treatment'
      }
    }
  },
  {
    id: 'create-treatment-plan-sikka-production',
    category: 'Practice Management Integration',
    group: 'Treatment Plans',
    name: 'Create Treatment Plan',
    method: 'POST',
    path: '/api/developer/practice/treatment-plan',
    description: 'Create a new treatment plan in the practice management system using a request_key, using the live environment.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      patient_id: { type: 'string', required: true, example: '18' },
      practice_id: { type: 'string', required: true, example: '1' },
      treatment_plan_name: { type: 'string', required: true, example: 'New Treatmentplan for all Treatment', maxLength: 40 },
      procedure_code: { type: 'string', required: true, example: 'D2510' },
      user: { type: 'string', required: true, example: '02' },
      amount: { type: 'string', required: false, example: '' },
      tooth: { type: 'string', required: false, example: '1' },
      surface: { type: 'string', required: false, example: 'D' },
      root: { type: 'string', required: false, example: '' },
      provider_id: { type: 'string', required: false, example: '' },
      entry_date: { type: 'string', required: false, example: '' },
      procedure_date: { type: 'string', required: false, example: '' },
      note: { type: 'string', required: false, example: '', maxLength: 250 },
      guarantor_id: { type: 'string', required: false, example: '' },
      attach_to_appointment: { type: 'string', required: false, example: 'false' },
      appointment_sr_no: { type: 'string', required: false, example: '857' },
      plan_sr_no: { type: 'string', required: false, example: '210648' },
      treatment_plan_heading: { type: 'string', required: false, example: '' },
      treatment_plan_type: { type: 'string', required: false, example: 'insurance' },
      priority: { type: 'string', required: false, example: '' }
    },
    responseExample: {
      success: true,
      data: {
        patient_id: '18',
        plan_sr_no: '210648',
        treatment_plan_name: 'New Treatmentplan for all Treatment'
      }
    }
  },
  {
    id: 'clinical-notes-sikka-sandbox',
    category: 'Practice Management Integration',
    group: 'Clinical Assessments and Notes',
    name: 'Get Clinical Notes',
    method: 'POST',
    path: '/api/developer/practice/medical-notes',
    description: 'Fetch a paginated list of clinical/medical notes from the practice management system using a request_key, using the sandbox environment.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        items: [
          { patient_id: '34', practice_id: '1-1', provider_id: 'DOC', note: 'Patient reports mild sensitivity on upper left molars.', date: '2024-01-15' }
        ],
        total_count: '9'
      }
    }
  },
  {
    id: 'clinical-notes-sikka-production',
    category: 'Practice Management Integration',
    group: 'Clinical Assessments and Notes',
    name: 'Get Clinical Notes',
    method: 'POST',
    path: '/api/developer/practice/medical-notes',
    description: 'Fetch a paginated list of clinical/medical notes from the practice management system using a request_key, using the live environment.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' }
    },
    responseExample: {
      success: true,
      data: {
        items: [
          { patient_id: '34', practice_id: '1-1', provider_id: 'DOC', note: 'Patient reports mild sensitivity on upper left molars.', date: '2024-01-15' }
        ],
        total_count: '9'
      }
    }
  },
  {
    id: 'note-types-sikka-sandbox',
    category: 'Practice Management Integration',
    group: 'Clinical Assessments and Notes',
    name: 'Note Types',
    method: 'POST',
    path: '/api/developer/practice/note-types',
    description: 'Fetch a paginated list of note types from the practice management system using a request_key, using the sandbox environment.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' },
      type: { type: 'string', required: false, example: '' },
      practice_id: { type: 'string', required: false, example: '1-1' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        items: [
          { note_type_id: '1', description: 'Clinical' }
        ],
        total_count: '6'
      }
    }
  },
  {
    id: 'note-types-sikka-production',
    category: 'Practice Management Integration',
    group: 'Clinical Assessments and Notes',
    name: 'Note Types',
    method: 'POST',
    path: '/api/developer/practice/note-types',
    description: 'Fetch a paginated list of note types from the practice management system using a request_key, using the live environment.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' },
      type: { type: 'string', required: false, example: '' },
      practice_id: { type: 'string', required: false, example: '1-1' }
    },
    responseExample: {
      success: true,
      data: {
        items: [
          { note_type_id: '1', description: 'Clinical' }
        ],
        total_count: '6'
      }
    }
  },
  {
    id: 'create-clinical-note-sikka-sandbox',
    category: 'Practice Management Integration',
    group: 'Clinical Assessments and Notes',
    name: 'Create Clinical Notes',
    method: 'POST',
    path: '/api/developer/practice/medical-note',
    description: 'Create a new clinical/medical note in the practice management system using a request_key, using the sandbox environment.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      patient_id: { type: 'string', required: true, example: '34' },
      practice_id: { type: 'string', required: true, example: '1-1' },
      provider_id: { type: 'string', required: true, example: 'DOC' },
      type: { type: 'string', required: true, example: 'Clinical' },
      date: { type: 'string', required: true, example: '2025-10-26' },
      text: { type: 'string', required: true, example: 'Patient reports mild sensitivity on upper left molars.' },
      description: { type: 'string', required: false, example: '' },
      appointment_sr_no: { type: 'string', required: false, example: '857' },
      item: { type: 'string', required: false, example: '' },
      acknowledge_type: { type: 'string', required: false, example: '' },
      staff: { type: 'string', required: false, example: '' },
      user: { type: 'string', required: false, example: '' },
      communication_type: { type: 'string', required: false, example: '' },
      mode: { type: 'string', required: false, example: '' },
      sent_or_received: { type: 'string', required: false, example: '' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        patient_id: '34',
        type: 'Clinical',
        date: '2025-10-26'
      }
    }
  },
  {
    id: 'create-clinical-note-sikka-production',
    category: 'Practice Management Integration',
    group: 'Clinical Assessments and Notes',
    name: 'Create Clinical Notes',
    method: 'POST',
    path: '/api/developer/practice/medical-note',
    description: 'Create a new clinical/medical note in the practice management system using a request_key, using the live environment.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      patient_id: { type: 'string', required: true, example: '34' },
      practice_id: { type: 'string', required: true, example: '1-1' },
      provider_id: { type: 'string', required: true, example: 'DOC' },
      type: { type: 'string', required: true, example: 'Clinical' },
      date: { type: 'string', required: true, example: '2025-10-26' },
      text: { type: 'string', required: true, example: 'Patient reports mild sensitivity on upper left molars.' },
      description: { type: 'string', required: false, example: '' },
      appointment_sr_no: { type: 'string', required: false, example: '857' },
      item: { type: 'string', required: false, example: '' },
      acknowledge_type: { type: 'string', required: false, example: '' },
      staff: { type: 'string', required: false, example: '' },
      user: { type: 'string', required: false, example: '' },
      communication_type: { type: 'string', required: false, example: '' },
      mode: { type: 'string', required: false, example: '' },
      sent_or_received: { type: 'string', required: false, example: '' }
    },
    responseExample: {
      success: true,
      data: {
        patient_id: '34',
        type: 'Clinical',
        date: '2025-10-26'
      }
    }
  },
  {
    id: 'patient-documents-sikka-sandbox',
    category: 'Practice Management Integration',
    group: 'Documents',
    name: 'Get Patient Documents',
    method: 'POST',
    path: '/api/developer/practice/patient-documents',
    description: 'Fetch a paginated list of patient documents from the practice management system using a request_key, using the sandbox environment.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' },
      practice_id: { type: 'string', required: false, example: '1-1' },
      patient_id: { type: 'string', required: false, example: '34' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        items: [
          { patient_id: '34', practice_id: '1-1', document_id: '512', document_name: 'Consent Form.pdf', date: '2024-01-15' }
        ],
        total_count: '7'
      }
    }
  },
  {
    id: 'patient-documents-sikka-production',
    category: 'Practice Management Integration',
    group: 'Documents',
    name: 'Get Patient Documents',
    method: 'POST',
    path: '/api/developer/practice/patient-documents',
    description: 'Fetch a paginated list of patient documents from the practice management system using a request_key, using the live environment.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      offset: { type: 'string', required: true, example: '1' },
      limit: { type: 'string', required: true, example: '10' },
      practice_id: { type: 'string', required: false, example: '1-1' },
      patient_id: { type: 'string', required: false, example: '34' }
    },
    responseExample: {
      success: true,
      data: {
        items: [
          { patient_id: '34', practice_id: '1-1', document_id: '512', document_name: 'Consent Form.pdf', date: '2024-01-15' }
        ],
        total_count: '7'
      }
    }
  },
  {
    id: 'create-document-sikka-sandbox',
    category: 'Practice Management Integration',
    group: 'Documents',
    name: 'Create Document',
    method: 'POST',
    path: '/api/developer/practice/document',
    description: 'Create a new patient document in the practice management system using a request_key, using the sandbox environment.',
    auth: 'token',
    environment: 'sandbox',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      patient_id: { type: 'string', required: true, example: '34' },
      practice_id: { type: 'string', required: true, example: '1-1' },
      document_type: { type: 'string', required: true, example: 'Consent Form' },
      document_name: { type: 'string', required: true, example: 'Consent Form.pdf' },
      created_datetime: { type: 'string', required: true, example: '2025-10-26T10:00:00' },
      type: { type: 'string', required: true, example: 'PDF' },
      description: { type: 'string', required: false, example: '' },
      tooth: { type: 'string', required: false, example: '' },
      provider_id: { type: 'string', required: false, example: 'DOC' },
      document_base64: { type: 'base64', required: false, example: '' },
      record_type: { type: 'string', required: false, example: '' }
    },
    responseExample: {
      success: true,
      environment: 'sandbox',
      data: {
        patient_id: '34',
        document_name: 'Consent Form.pdf'
      }
    }
  },
  {
    id: 'create-document-sikka-production',
    category: 'Practice Management Integration',
    group: 'Documents',
    name: 'Create Document',
    method: 'POST',
    path: '/api/developer/practice/document',
    description: 'Create a new patient document in the practice management system using a request_key, using the live environment.',
    auth: 'token',
    environment: 'production',
    requestBody: {
      request_key: { type: 'string', required: true, example: '6fbb49d18c4216468721da297a8a2b31' },
      patient_id: { type: 'string', required: true, example: '34' },
      practice_id: { type: 'string', required: true, example: '1-1' },
      document_type: { type: 'string', required: true, example: 'Consent Form' },
      document_name: { type: 'string', required: true, example: 'Consent Form.pdf' },
      created_datetime: { type: 'string', required: true, example: '2025-10-26T10:00:00' },
      type: { type: 'string', required: true, example: 'PDF' },
      description: { type: 'string', required: false, example: '' },
      tooth: { type: 'string', required: false, example: '' },
      provider_id: { type: 'string', required: false, example: 'DOC' },
      document_base64: { type: 'base64', required: false, example: '' },
      record_type: { type: 'string', required: false, example: '' }
    },
    responseExample: {
      success: true,
      data: {
        patient_id: '34',
        document_name: 'Consent Form.pdf'
      }
    }
  }
];

// Method color mapping
const METHOD_COLORS = {
  GET: 'bg-green-500',
  POST: 'bg-blue-500',
  PUT: 'bg-yellow-500',
  DELETE: 'bg-red-500',
  PATCH: 'bg-purple-500'
};

// Get initial endpoint based on stored mode (defaults to sandbox)
const getInitialEndpoint = () => {
  const storedMode = localStorage.getItem('apiPlaygroundMode') || 'sandbox';
  return API_ENDPOINTS.find(ep => ep.environment === storedMode) || API_ENDPOINTS[0];
};

export default function ApiPlayground() {
  const [selectedEndpoint, setSelectedEndpoint] = useState(getInitialEndpoint());
  const [requestParams, setRequestParams] = useState({});
  const [headerParams, setHeaderParams] = useState({});
  const [pathParams, setPathParams] = useState({});
  const [accessToken, setAccessToken] = useState('');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fileWarnings, setFileWarnings] = useState({});
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('playground'); // playground, code
  const [expandedCategories, setExpandedCategories] = useState(['Authentication', 'Sessions', 'Appointments', 'Patients', 'Providers', 'Patient Medical History', 'Practice Management Integration']);
  const [showToken, setShowToken] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [apiMode, setApiMode] = useState(() => {
    // Initialize from localStorage, default to 'sandbox'
    return localStorage.getItem('apiPlaygroundMode') || 'sandbox';
  });

  // Domain security state
  const [allowedDomains, setAllowedDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(true);

  // Fetch domain settings
  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const res = await api.get('/api/settings/domains');
        setAllowedDomains(res.data.allowed_domains || []);
      } catch (err) {
        console.error('Failed to fetch domain settings:', err);
        // If not authenticated or error, assume no domains
        setAllowedDomains([]);
      } finally {
        setDomainsLoading(false);
      }
    };
    fetchDomains();
  }, []);

  // Check if user has localhost domain (for sandbox warning)
  const hasLocalhostDomain = allowedDomains.some(d => 
    d.toLowerCase().includes('localhost') || d.includes('127.0.0.1')
  );

  // Check if user has non-localhost domain (for production)
  const hasNonLocalhostDomain = allowedDomains.some(d => 
    !d.toLowerCase().includes('localhost') && !d.includes('127.0.0.1')
  );

  // Persist apiMode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('apiPlaygroundMode', apiMode);
  }, [apiMode]);

  // Reset selected endpoint when apiMode changes
  useEffect(() => {
    // Find first matching endpoint for the new mode
    const matchingEndpoint = API_ENDPOINTS.find(ep => !ep.environment || ep.environment === apiMode);
    if (matchingEndpoint && (!selectedEndpoint.environment || selectedEndpoint.environment !== apiMode)) {
      // Only reset if current selection doesn't match the new mode
      const currentMatchesMode = !selectedEndpoint.environment || selectedEndpoint.environment === apiMode;
      if (!currentMatchesMode) {
        setSelectedEndpoint(matchingEndpoint);
      }
    }
  }, [apiMode]);

  // Initialize request params when endpoint changes
  useEffect(() => {
    if (selectedEndpoint.requestBody) {
      const initialParams = {};
      Object.entries(selectedEndpoint.requestBody).forEach(([key, config]) => {
        // Initialize with appropriate default based on type
        if (config.type === 'boolean') {
          initialParams[key] = false;
        } else if (config.type === 'array') {
          initialParams[key] = '';  // Will be parsed as comma-separated
        } else if (config.type === 'select') {
          initialParams[key] = config.required && config.options?.length ? config.options[0] : '';
        } else if (config.type === 'file') {
          initialParams[key] = config.multiple ? [] : null;
        } else {
          initialParams[key] = '';  // Empty - no prefilled values
        }
      });
      setRequestParams(initialParams);
    } else {
      setRequestParams({});
    }

    if (selectedEndpoint.customHeaders) {
      const initialHeaders = {};
      Object.keys(selectedEndpoint.customHeaders).forEach((key) => {
        initialHeaders[key] = '';
      });
      setHeaderParams(initialHeaders);
    } else {
      setHeaderParams({});
    }

    if (selectedEndpoint.pathParams) {
      const initialPathParams = {};
      Object.entries(selectedEndpoint.pathParams).forEach(([key, config]) => {
        initialPathParams[key] = '';  // Empty - no prefilled values
      });
      setPathParams(initialPathParams);
    } else {
      setPathParams({});
    }

    setResponse(null);
    setError(null);
  }, [selectedEndpoint]);

  // Build the actual URL with path params
  // For data endpoints (auth: 'token'), swap /api/developer/ with /api/sandbox/ when in sandbox mode
  const buildUrl = () => {
    let url = selectedEndpoint.path;
    
    // Swap API path based on mode for data endpoints (not auth endpoints)
    if (apiMode === 'sandbox' && selectedEndpoint.auth === 'token') {
      url = url.replace('/api/developer/', '/api/sandbox/');
    }
    
    if (selectedEndpoint.pathParams) {
      Object.entries(pathParams).forEach(([key, value]) => {
        url = url.replace(`{${key}}`, value || `{${key}}`);
      });
    }
    return url;
  };

  // Execute API request
  const executeRequest = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const url = buildUrl();
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const fullUrl = `${baseUrl}${url}`;

      const headers = {};

      // Only set Content-Type for non-form-data requests
      if (!selectedEndpoint.isFormData) {
        headers['Content-Type'] = 'application/json';
      }

      if (selectedEndpoint.auth === 'token' && accessToken) {
        headers['X-Developer-Token'] = accessToken;
      }
      Object.entries(headerParams).forEach(([key, value]) => {
        if (value) headers[key] = value;
      });

      const options = {
        method: selectedEndpoint.method,
        headers
      };

      if (selectedEndpoint.requestBody && ['POST', 'PUT', 'PATCH'].includes(selectedEndpoint.method)) {
        if (selectedEndpoint.isFormData) {
          // Use FormData for form-data endpoints
          const formData = new FormData();
          Object.entries(requestParams).forEach(([key, value]) => {
            if (value === null || value === undefined) return;
            if (Array.isArray(value)) {
              value.forEach((item) => formData.append(key, item));
            } else {
              formData.append(key, value);
            }
          });
          options.body = formData;
        } else {
          // Process request params based on type definitions
          const processedParams = {};
          Object.entries(requestParams).forEach(([key, value]) => {
            const config = selectedEndpoint.requestBody[key];
            if (config?.type === 'array' && typeof value === 'string') {
              // Parse comma-separated string to array, trim whitespace
              processedParams[key] = value.split(',').map(v => v.trim()).filter(v => v);
            } else if (config?.type === 'boolean') {
              processedParams[key] = value === true || value === 'true';
            } else {
              processedParams[key] = value;
            }
          });
          options.body = JSON.stringify(processedParams);
        }
      }

      const startTime = Date.now();
      const res = await fetch(fullUrl, options);
      const endTime = Date.now();

      const data = await res.json();

      setResponse({
        status: res.status,
        statusText: res.statusText,
        time: endTime - startTime,
        data
      });

      // Auto-save access token if this was a token generation request
      if ((selectedEndpoint.id === 'token' || selectedEndpoint.id === 'sandbox-token') && data.access_token) {
        setAccessToken(data.access_token);
      }
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  // Generate code examples
  const generateCodeExample = (language) => {
    const url = buildUrl();
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const fullUrl = `${baseUrl}${url}`;
    const isFormData = selectedEndpoint.isFormData;

    const configuredHeaders = selectedEndpoint.customHeaders || {};
    const getHeaderExample = (key) => headerParams[key] || configuredHeaders[key].example || '';

    // Process request params based on type definitions (for code examples)
    const processedParams = {};
    if (selectedEndpoint.requestBody) {
      Object.entries(requestParams).forEach(([key, value]) => {
        const config = selectedEndpoint.requestBody[key];
        if (config?.type === 'array' && typeof value === 'string') {
          // Parse comma-separated string to array
          processedParams[key] = value.split(',').map(v => v.trim()).filter(v => v);
        } else if (config?.type === 'boolean') {
          processedParams[key] = value === true || value === 'true';
        } else {
          processedParams[key] = value;
        }
      });
    }

    if (language === 'curl') {
      let cmd = `curl -X ${selectedEndpoint.method} "${fullUrl}"`;
      if (selectedEndpoint.auth === 'token') {
        cmd += ` \\\n  -H "X-Developer-Token: YOUR_ACCESS_TOKEN"`;
      }
      Object.keys(configuredHeaders).forEach((key) => {
        cmd += ` \\\n  -H "${key}: ${getHeaderExample(key)}"`;
      });
      if (selectedEndpoint.requestBody) {
        if (isFormData) {
          Object.entries(selectedEndpoint.requestBody).forEach(([key, config]) => {
            if (config?.type === 'file') {
              const count = config.multiple ? (config.maxFiles || 3) : 1;
              for (let i = 1; i <= count; i++) {
                const placeholder = count > 1 ? `/path/to/file${i}.pdf` : '/path/to/file.pdf';
                cmd += ` \\\n  -F "${key}=@${placeholder};type=${config.accept || 'application/octet-stream'}"`;
              }
            } else {
              cmd += ` \\\n  -F "${key}=${requestParams[key] ?? ''}"`;
            }
          });
        } else {
          cmd += ` \\\n  -H "Content-Type: application/json"`;
          cmd += ` \\\n  -d '${JSON.stringify(processedParams, null, 2)}'`;
        }
      }
      return cmd;
    }

    if (language === 'python') {
      let code = `import requests\n\n`;
      code += `url = "${fullUrl}"\n`;
      if (selectedEndpoint.auth === 'token') {
        code += `headers = {"X-Developer-Token": "YOUR_ACCESS_TOKEN"}\n`;
      } else {
        code += `headers = {}\n`;
      }
      Object.keys(configuredHeaders).forEach((key) => {
        code += `headers["${key}"] = "${getHeaderExample(key)}"\n`;
      });
      if (selectedEndpoint.requestBody) {
        const fileFields = Object.entries(selectedEndpoint.requestBody).filter(([, config]) => config?.type === 'file');
        const nonFileParams = { ...processedParams };
        fileFields.forEach(([key]) => { delete nonFileParams[key]; });
        code += `data = ${JSON.stringify(nonFileParams, null, 4)}\n\n`;
        if (isFormData) {
          if (fileFields.length > 0) {
            const fileEntries = [];
            fileFields.forEach(([key, config]) => {
              const count = config.multiple ? (config.maxFiles || 3) : 1;
              for (let i = 1; i <= count; i++) {
                const placeholder = count > 1 ? `/path/to/file${i}.pdf` : '/path/to/file.pdf';
                fileEntries.push(`("${key}", open("${placeholder}", "rb"))`);
              }
            });
            code += `files = [${fileEntries.join(', ')}]\n`;
            code += `response = requests.${selectedEndpoint.method.toLowerCase()}(url, headers=headers, data=data, files=files)\n`;
          } else {
            code += `response = requests.${selectedEndpoint.method.toLowerCase()}(url, headers=headers, data=data)\n`;
          }
        } else {
          code += `response = requests.${selectedEndpoint.method.toLowerCase()}(url, headers=headers, json=data)\n`;
        }
      } else {
        code += `\nresponse = requests.${selectedEndpoint.method.toLowerCase()}(url, headers=headers)\n`;
      }
      code += `print(response.json())`;
      return code;
    }

    if (language === 'javascript') {
      let code = '';
      if (isFormData && selectedEndpoint.requestBody) {
        code += `const formData = new FormData();\n`;
        Object.entries(selectedEndpoint.requestBody).forEach(([key, config]) => {
          if (config?.type === 'file') {
            if (config.multiple) {
              code += `// <input type="file" multiple> — attach up to ${config.maxFiles || 3} files\n`;
              code += `for (const f of fileInput.files) formData.append("${key}", f);\n`;
            } else {
              code += `formData.append("${key}", fileInput.files[0]); // <input type="file">\n`;
            }
          } else {
            code += `formData.append("${key}", "${requestParams[key] ?? ''}");\n`;
          }
        });
        code += `\n`;
      }
      code += `const response = await fetch("${fullUrl}", {\n`;
      code += `  method: "${selectedEndpoint.method}",\n`;
      const javascriptHeaders = [];
      if (selectedEndpoint.auth === 'token') {
        javascriptHeaders.push(`    "X-Developer-Token": "YOUR_ACCESS_TOKEN"`);
      }
      if (!isFormData && selectedEndpoint.requestBody) {
        javascriptHeaders.push(`    "Content-Type": "application/json"`);
      }
      Object.keys(configuredHeaders).forEach((key) => {
        javascriptHeaders.push(`    "${key}": "${getHeaderExample(key)}"`);
      });
      code += `  headers: {\n${javascriptHeaders.join(',\n')}\n  }`;
      if (selectedEndpoint.requestBody) {
        if (isFormData) {
          code += `,\n  body: formData`;
        } else {
          code += `,\n  body: JSON.stringify(${JSON.stringify(processedParams, null, 4).split('\n').join('\n  ')})`;
        }
      }
      code += `\n});\n\nconst data = await response.json();\nconsole.log(data);`;
      return code;
    }

    if (language === 'php') {
      let code = `<?php\n\n`;
      code += `$url = "${fullUrl}";\n\n`;
      code += `$headers = [\n`;
      if (selectedEndpoint.auth === 'token') {
        code += `    "X-Developer-Token: YOUR_ACCESS_TOKEN",\n`;
      }
      Object.keys(configuredHeaders).forEach((key) => {
        code += `    "${key}: ${getHeaderExample(key)}",\n`;
      });
      if (!isFormData) {
        code += `    "Content-Type: application/json",\n`;
      }
      code += `    "Accept: application/json"\n`;
      code += `];\n\n`;
      code += `$ch = curl_init();\n`;
      code += `curl_setopt($ch, CURLOPT_URL, $url);\n`;
      code += `curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);\n`;
      code += `curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);\n`;
      if (selectedEndpoint.method !== 'GET') {
        code += `curl_setopt($ch, CURLOPT_CUSTOMREQUEST, "${selectedEndpoint.method}");\n`;
      }
      if (selectedEndpoint.requestBody) {
        if (isFormData) {
          const phpFileFields = Object.entries(selectedEndpoint.requestBody).filter(([, config]) => config?.type === 'file');
          const phpNonFileParams = { ...requestParams };
          phpFileFields.forEach(([key]) => { delete phpNonFileParams[key]; });
          code += `\n$data = ${JSON.stringify(phpNonFileParams, null, 4).split('\n').map((line, i) => i === 0 ? line : '    ' + line).join('\n').replace(/"/g, "'").replace(/: /g, ' => ')};\n`;
          phpFileFields.forEach(([key, config]) => {
            if (config.multiple) {
              code += `// Repeated field names aren't supported by a plain PHP array; send each file as "${key}[]"\n`;
              code += `// and have the server accept either "${key}" or "${key}[]" as the form field name.\n`;
              const count = config.maxFiles || 3;
              for (let i = 0; i < count; i++) {
                code += `$data['${key}[${i}]'] = new CURLFile('/path/to/file${i + 1}.pdf', '${config.accept || 'application/octet-stream'}');\n`;
              }
            } else {
              code += `$data['${key}'] = new CURLFile('/path/to/file.pdf', '${config.accept || 'application/octet-stream'}');\n`;
            }
          });
          code += `curl_setopt($ch, CURLOPT_POSTFIELDS, $data);\n`;
        } else {
          code += `\n$data = ${JSON.stringify(processedParams, null, 4).split('\n').map((line, i) => i === 0 ? line : '    ' + line).join('\n').replace(/"/g, "'").replace(/: /g, ' => ')};\n`;
          code += `curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));\n`;
        }
      }
      code += `\n$response = curl_exec($ch);\n`;
      code += `curl_close($ch);\n\n`;
      code += `$result = json_decode($response, true);\n`;
      code += `print_r($result);\n`;
      return code;
    }

    return '';
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  // Group endpoints by category, filtering based on apiMode
  const groupedEndpoints = API_ENDPOINTS
    .filter(endpoint => {
      // Hide the Practice Management Integration category when the feature flag is off
      if (endpoint.category === 'Practice Management Integration' && !PRACTICE_MANAGEMENT_INTEGRATION_ENABLED) {
        return false;
      }
      // If endpoint has no environment specified, show it in both modes
      if (!endpoint.environment) return true;
      // Otherwise, only show if it matches the current mode
      return endpoint.environment === apiMode;
    })
    .reduce((acc, endpoint) => {
      if (!acc[endpoint.category]) {
        acc[endpoint.category] = [];
      }
      acc[endpoint.category].push(endpoint);
      return acc;
    }, {});

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 flex flex-col">
      {/* Header Bar - Desktop */}
      <div className="hidden lg:flex bg-white border-b border-gray-200 px-6 py-3 items-center justify-between fixed top-0 left-0 right-0 z-50 shadow-sm">
        <Link 
          to="/dashboard" 
          className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <BookOpen className="w-4 h-4 text-blue-600" />
          <span className="font-medium text-gray-700">Developer API Reference</span>
        </div>
      </div>

      {/* Spacer for fixed header - Desktop only */}
      <div className="hidden lg:block h-12"></div>

      {/* Main content wrapper */}
      <div className="flex-1 flex flex-col lg:flex-row">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-gray-200 p-4 flex flex-col sticky top-0 z-20">
        {/* Mobile Back Link */}
        <Link 
          to="/dashboard" 
          className="inline-flex items-center gap-1 text-gray-600 hover:text-blue-600 transition-colors text-sm mb-2"
        >
          <ArrowLeft className="w-3 h-3" />
          Back to Dashboard
        </Link>
        <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-blue-600" />
          API Reference
        </h1>
        <div className="flex items-center gap-3">
          {/* Mobile Sandbox/Live Toggle */}
          <button
            onClick={() => setApiMode(apiMode === 'sandbox' ? 'production' : 'sandbox')}
            className={`px-2 py-1 text-xs font-medium rounded-full ${
              apiMode === 'sandbox' 
                ? 'bg-green-100 text-green-700 border border-green-300' 
                : 'bg-green-600 text-white border border-green-600'
            }`}
          >
            {apiMode === 'sandbox' ? 'Sandbox' : 'Live'}
          </button>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-md hover:bg-gray-100"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        </div>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Endpoint List */}
      <div className={`
        fixed lg:fixed inset-y-0 left-0 z-40
        lg:top-12
        w-72 lg:w-80 bg-white border-r border-gray-200 overflow-y-auto
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-4 border-b border-gray-200 hidden lg:block">
          <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-600" />
            Developer API Endpoints
          </h1>
          
          {/* Sandbox/Live Toggle */}
          <div className="mt-3 flex items-center gap-2">
            <span className={`text-xs font-medium ${apiMode === 'sandbox' ? 'text-green-600' : 'text-gray-400'}`}>
              Sandbox
            </span>
            <button
              onClick={() => setApiMode(apiMode === 'sandbox' ? 'production' : 'sandbox')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                apiMode === 'production' ? 'bg-green-600' : 'bg-green-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  apiMode === 'production' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-xs font-medium ${apiMode === 'production' ? 'text-green-600' : 'text-gray-400'}`}>
              Live
            </span>
          </div>
        </div>

        {/* Mobile sidebar header */}
        <div className="p-4 border-b border-gray-200 lg:hidden flex items-center justify-between">
          <h1 className="text-lg font-bold text-gray-900">Endpoints</h1>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 rounded-md hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Endpoint List */}
        <div className="py-2">
          {Object.entries(groupedEndpoints).map(([category, endpoints]) => {
            const ungroupedEndpoints = endpoints.filter((endpoint) => !endpoint.group);
            const subGroups = endpoints.reduce((acc, endpoint) => {
              if (endpoint.group) {
                if (!acc[endpoint.group]) acc[endpoint.group] = [];
                acc[endpoint.group].push(endpoint);
              }
              return acc;
            }, {});

            const renderEndpointButton = (endpoint, indent = false) => (
              <button
                key={endpoint.id}
                onClick={() => {
                  setSelectedEndpoint(endpoint);
                  setSidebarOpen(false);
                }}
                className={`w-full ${indent ? 'pl-7 pr-4' : 'px-4'} py-2 flex items-center gap-3 text-left hover:bg-gray-100 transition ${
                  selectedEndpoint.id === endpoint.id ? 'bg-blue-50 border-l-2 border-blue-500' : ''
                }`}
              >
                <span className={`px-2 py-0.5 text-xs font-bold rounded ${METHOD_COLORS[endpoint.method]} text-white`}>
                  {endpoint.method}
                </span>
                <span className="text-sm text-gray-700 truncate">{endpoint.name}</span>
                {endpoint.paid && apiMode !== 'sandbox' && (
                  <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-green-100 text-green-700 flex-shrink-0">Paid</span>
                )}
                {endpoint.auth === 'token' && (
                  <Lock className="w-3 h-3 text-amber-500 ml-auto flex-shrink-0" />
                )}
              </button>
            );

            return (
              <div key={category}>
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full px-4 py-2 flex items-center justify-between text-sm font-semibold text-gray-700 hover:bg-gray-100"
                >
                  <span>{category}</span>
                  {expandedCategories.includes(category) ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>

                {expandedCategories.includes(category) && (
                  <div className="pb-2">
                    {ungroupedEndpoints.map((endpoint) => renderEndpointButton(endpoint))}

                    {Object.entries(subGroups).map(([groupName, groupEndpoints]) => (
                      <div key={groupName}>
                        <div className="px-4 pt-2 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                          {groupName}
                        </div>
                        {groupEndpoints.map((endpoint) => renderEndpointButton(endpoint, true))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden lg:ml-80">
        {/* Endpoint Header */}
        <div className="bg-white border-b border-gray-200 p-4 lg:p-6">
          {/* Domain Security Warning */}
          {!domainsLoading && allowedDomains.length === 0 && (selectedEndpoint.id === 'credentials' || selectedEndpoint.id === 'sandbox-credentials' || selectedEndpoint.id === 'regenerate-secret' || selectedEndpoint.id === 'sandbox-regenerate-secret') && (
            <div className="mb-3 px-4 py-3 bg-amber-50 border border-amber-300 rounded-lg">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800">Domain Configuration Required</p>
                  <p className="text-sm text-amber-700 mt-1">
                    You must configure at least one authorized domain before generating API credentials.
                    This helps protect your API keys from unauthorized use.
                  </p>
                  <Link 
                    to="/dashboard/settings/api-security"
                    className="inline-flex items-center gap-1 mt-2 text-sm font-medium text-amber-700 hover:text-amber-900 underline"
                  >
                    Configure Domains →
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Production Mode Warning - No Non-Localhost Domains */}
          {!domainsLoading && apiMode === 'production' && allowedDomains.length > 0 && !hasNonLocalhostDomain && (selectedEndpoint.id === 'credentials' || selectedEndpoint.id === 'regenerate-secret') && (
            <div className="mb-3 px-4 py-3 bg-red-50 border border-red-300 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800">Production Domains Required</p>
                  <p className="text-sm text-red-700 mt-1">
                    Localhost domains are not allowed for Live credentials. 
                    Please add a production domain (e.g., https://myapp.com) to generate Live API credentials.
                  </p>
                  <Link 
                    to="/dashboard/settings/api-security"
                    className="inline-flex items-center gap-1 mt-2 text-sm font-medium text-red-700 hover:text-red-900 underline"
                  >
                    Add Production Domain →
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Mode Banner */}
          <div className={`mb-3 px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${
            apiMode === 'sandbox' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-green-100 border border-green-300 text-green-900'
          }`}>
            <div className={`w-2 h-2 rounded-full ${apiMode === 'sandbox' ? 'bg-green-600' : 'bg-green-600'}`} />
            <span className="font-medium">{apiMode === 'sandbox' ? 'Sandbox Mode' : 'Live Mode'}</span>
          </div>
          
          <div className="flex flex-wrap items-center gap-2 lg:gap-3 mb-2">
            <span className={`px-2 lg:px-3 py-1 text-xs lg:text-sm font-bold rounded ${METHOD_COLORS[selectedEndpoint.method]} text-white`}>
              {selectedEndpoint.method}
            </span>
            <code className="text-sm lg:text-lg text-gray-700 font-mono bg-gray-100 px-2 py-1 rounded break-all">{buildUrl()}</code>
            {selectedEndpoint.auth === 'token' ? (
              <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                <Lock className="w-3 h-3" /> <span className="hidden sm:inline">Requires</span> Token
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded border border-green-200">
                <Unlock className="w-3 h-3" /> Public
              </span>
            )}
          </div>
          <h2 className="text-xl lg:text-2xl font-bold text-gray-900">
            {selectedEndpoint.name}
            {selectedEndpoint.paid && apiMode !== 'sandbox' && (
              <span className="ml-2 px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-700">Paid</span>
            )}
          </h2>
          <p className="text-sm lg:text-base text-gray-600 mt-1">{selectedEndpoint.description}</p>
          
          {/* Sandbox Secret Expiration Warning */}
          {(selectedEndpoint.id === 'sandbox-credentials' || selectedEndpoint.id === 'sandbox-regenerate-secret') && (
            <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="text-sm">
                <span className="font-semibold text-amber-800">Your sandbox secret is valid for 30 days</span>
                <p className="text-amber-700 mt-0.5">After it expires, you'll need to generate a new secret </p>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white border-b border-gray-200 px-4 lg:px-6">
          <div className="flex gap-4 overflow-x-auto">
            <button
              onClick={() => setActiveTab('playground')}
              className={`py-3 px-1 border-b-2 transition whitespace-nowrap ${
                activeTab === 'playground'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="flex items-center gap-2">
                Playground
              </span>
            </button>
            <button
              onClick={() => setActiveTab('code')}
              className={`py-3 px-1 border-b-2 transition whitespace-nowrap ${
                activeTab === 'code'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="flex items-center gap-2">
                <Code className="w-4 h-4" /> Code Examples
              </span>
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-6 bg-gray-50">
          {activeTab === 'playground' ? (
            <div className="flex flex-col gap-4 h-full">
              {/* Access Token Input */}
              <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Access Token
                </label>
                <div className="relative">
                  <input
                    type={showToken ? 'text' : 'password'}
                    value={accessToken}
                    onChange={(e) => setAccessToken(e.target.value)}
                    placeholder="Enter your access token"
                    autoComplete="off"
                    className="w-full px-3 py-2 pr-10 bg-gray-50 border border-gray-300 rounded text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowToken(!showToken)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showToken ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Required for protected endpoints. Generate a token using the "Generate Access Token" endpoint.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 flex-1">
              {/* Request Panel */}
              <div className="bg-white rounded-lg border border-gray-200 flex flex-col shadow-sm min-h-[300px] lg:min-h-0">
                <div className="px-4 py-3 border-b border-gray-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <h3 className="font-semibold text-gray-900">Request</h3>
                  <button
                    onClick={executeRequest}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white rounded transition w-full sm:w-auto justify-center"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Executing...
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4" /> Execute
                      </>
                    )}
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                  {/* Path Parameters */}
                  {selectedEndpoint.pathParams && (
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Path Parameters</h4>
                      {Object.entries(selectedEndpoint.pathParams).map(([key, config]) => (
                        <div key={key} className="mb-3">
                          <label className="block text-sm text-gray-600 mb-1">
                            {key}
                            {config.required && <span className="text-red-500 ml-1">*</span>}
                            <span className="text-gray-400 ml-2 text-xs">({config.type})</span>
                          </label>
                          <input
                            type="text"
                            value={pathParams[key] || ''}
                            onChange={(e) => setPathParams({ ...pathParams, [key]: e.target.value })}
                            placeholder={`Enter ${key}`}
                            autoComplete="off"
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Custom Headers */}
                  {selectedEndpoint.customHeaders && (
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Headers</h4>
                      {Object.entries(selectedEndpoint.customHeaders).map(([key, config]) => (
                        <div key={key} className="mb-3">
                          <label className="block text-sm text-gray-600 mb-1">
                            {key}
                            {config.required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          <input
                            type="password"
                            value={headerParams[key] || ''}
                            onChange={(e) => setHeaderParams({ ...headerParams, [key]: e.target.value })}
                            placeholder={config.example || `Enter ${key}`}
                            autoComplete="off"
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Request Body */}
                  {selectedEndpoint.requestBody && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Request Body</h4>
                      {Object.entries(selectedEndpoint.requestBody).map(([key, config]) => (
                        <div key={key} className="mb-3">
                          <label className="block text-sm text-gray-600 mb-1">
                            {key}
                            {config.required && <span className="text-red-500 ml-1">*</span>}
                            <span className="text-gray-400 ml-2 text-xs">({config.type})</span>
                          </label>
                          {config.type === 'boolean' ? (
                            <select
                              value={requestParams[key] === true ? 'true' : 'false'}
                              onChange={(e) => setRequestParams({ ...requestParams, [key]: e.target.value === 'true' })}
                              className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded text-sm text-gray-900 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="false">false</option>
                              <option value="true">true</option>
                            </select>
                          ) : config.type === 'select' ? (
                            <select
                              value={requestParams[key] || ''}
                              onChange={(e) => setRequestParams({ ...requestParams, [key]: e.target.value })}
                              className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded text-sm text-gray-900 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            >
                              {!config.required && <option value="">-- Select {key} --</option>}
                              {(config.options || []).map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : config.type === 'array' ? (
                            <>
                              <input
                                type="text"
                                value={requestParams[key] || ''}
                                onChange={(e) => setRequestParams({ ...requestParams, [key]: e.target.value })}
                                placeholder={config.example ? `e.g. ${config.example.join(', ')}` : `Enter ${key} (comma-separated)`}
                                autoComplete="off"
                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                              />
                              <p className="text-xs text-gray-400 mt-1">{config.description || 'Enter values separated by commas'}</p>
                            </>
                          ) : config.type === 'file' ? (
                            <>
                              <input
                                type="file"
                                id={`file-input-${key}`}
                                accept={config.accept}
                                multiple={!!config.multiple}
                                onChange={(e) => {
                                  const selected = Array.from(e.target.files || []);
                                  const tooMany = config.multiple && config.maxFiles && selected.length > config.maxFiles;
                                  setFileWarnings((prev) => ({
                                    ...prev,
                                    [key]: tooMany
                                      ? `You selected ${selected.length} files, but only the first ${config.maxFiles} will be used. Max allowed is ${config.maxFiles}.`
                                      : null,
                                  }));
                                  if (config.multiple) {
                                    const limited = config.maxFiles ? selected.slice(0, config.maxFiles) : selected;
                                    setRequestParams({ ...requestParams, [key]: limited });
                                  } else {
                                    setRequestParams({ ...requestParams, [key]: selected[0] || null });
                                  }
                                }}
                                className="hidden"
                              />
                              <div className="flex items-center gap-3">
                                <label
                                  htmlFor={`file-input-${key}`}
                                  className="cursor-pointer py-2 px-3 rounded bg-blue-50 text-blue-700 text-sm hover:bg-blue-100"
                                >
                                  Choose Files
                                </label>
                                <span className="text-sm text-gray-500">
                                  {config.multiple
                                    ? (Array.isArray(requestParams[key]) && requestParams[key].length > 0
                                        ? `${requestParams[key].length} file${requestParams[key].length > 1 ? 's' : ''} selected`
                                        : 'No files chosen')
                                    : (requestParams[key]?.name || 'No file chosen')}
                                </span>
                              </div>
                              {fileWarnings[key] && (
                                <p className="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                                  {fileWarnings[key]}
                                </p>
                              )}
                              {config.multiple && Array.isArray(requestParams[key]) && requestParams[key].length > 0 && (
                                <ul className="mt-1 text-xs text-gray-500 list-disc list-inside">
                                  {requestParams[key].map((f, i) => (
                                    <li key={i}>{f.name} ({(f.size / (1024 * 1024)).toFixed(2)}MB)</li>
                                  ))}
                                </ul>
                              )}
                              {config.description && (
                                <p className="text-xs text-gray-400 mt-1">
                                  {config.description}
                                  {config.maxFiles && ` — up to ${config.maxFiles} files`}
                                </p>
                              )}
                            </>
                          ) : config.type === 'textarea' ? (
                            <textarea
                              value={requestParams[key] || ''}
                              onChange={(e) => setRequestParams({ ...requestParams, [key]: e.target.value })}
                              placeholder={config.placeholder || `Enter ${key}`}
                              rows={4}
                              maxLength={config.maxLength}
                              className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-y"
                            />
                          ) : config.type === 'base64' ? (
                            <>
                              <textarea
                                value={requestParams[key] || ''}
                                onChange={(e) => setRequestParams({ ...requestParams, [key]: e.target.value })}
                                placeholder={config.placeholder || `Paste base64-encoded ${key}`}
                                rows={6}
                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 font-mono resize-y"
                              />
                              <p className="text-xs text-gray-400 mt-1">
                                {config.description || 'Maximum 2MB of total converted string, it includes any special characters in base64 string after proper escaping from api side.'}
                              </p>
                            </>
                          ) : (
                            <input
                              type={config.isPassword ? 'password' : 'text'}
                              value={requestParams[key] || ''}
                              onChange={(e) => setRequestParams({ ...requestParams, [key]: e.target.value })}
                              placeholder={config.placeholder || `Enter ${key}`}
                              autoComplete="off"
                              maxLength={config.maxLength}
                              className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            />
                          )}
                          {config.description && config.type !== 'array' && config.type !== 'file' && config.type !== 'base64' && (
                            <p className="text-xs text-gray-400 mt-1">{config.description}</p>
                          )}
                          {config.maxLength && (
                            <p className="text-xs text-gray-400 mt-1">
                              {(requestParams[key] || '').length}/{config.maxLength} characters
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {!selectedEndpoint.requestBody && !selectedEndpoint.pathParams && !selectedEndpoint.customHeaders && (
                    <p className="text-gray-500 text-sm">No parameters required</p>
                  )}
                </div>
              </div>

              {/* Response Panel */}
              <div className="bg-white rounded-lg border border-gray-200 flex flex-col shadow-sm min-h-[300px] lg:min-h-0">
                <div className="px-4 py-3 border-b border-gray-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                  <h3 className="font-semibold text-gray-900">Response</h3>
                  {response && (
                    <div className="flex flex-wrap items-center gap-2 lg:gap-3 text-sm">
                      <span className={`px-2 py-0.5 rounded ${
                        response.status >= 200 && response.status < 300
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {response.status} {response.statusText}
                      </span>
                      <span className="flex items-center gap-1 text-gray-500">
                        <Clock className="w-3 h-3" /> {response.time}ms
                      </span>
                      <button
                        onClick={() => copyToClipboard(JSON.stringify(response.data, null, 2))}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                  {error ? (
                    <div className="text-red-700 bg-red-50 p-4 rounded border border-red-200">
                      <p className="font-semibold">Error</p>
                      <p className="text-sm mt-1 break-words">{error}</p>
                    </div>
                  ) : response ? (
                    <div>
                      {/* One-time view warning for credentials */}
                      {response.data?.client_secret && (
                        <div className="mb-4 p-4 bg-amber-50 border border-amber-300 rounded-lg">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold text-amber-800">Important: Save Your Credentials</p>
                              <p className="text-sm text-amber-700 mt-1">
                                This is the only time your Client Secret will be displayed. Be sure to copy and securely store it now, as it cannot be viewed again later.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Access token expiration notice */}
                      {response.data?.access_token && (
                        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-start gap-3">
                            <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold text-blue-800">Access Token Generated</p>
                              <p className="text-sm text-blue-700 mt-1">
                                Your access token is valid for 30 minutes only. After expiration, you'll need to generate a new token using your client credentials.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      <pre className="text-xs sm:text-sm text-gray-800 font-mono whitespace-pre-wrap break-words bg-gray-50 p-3 lg:p-4 rounded overflow-x-auto">
                        {JSON.stringify(response.data, null, 2)}
                      </pre>
                    </div>
                  ) : (
                    <div className="text-gray-500 text-sm">
                      <p className="mb-4">Click "Execute" to run the request</p>
                      <p className="text-xs text-gray-400">Example Response:</p>
                      <pre className="mt-2 text-xs text-gray-500 font-mono whitespace-pre-wrap break-words bg-gray-50 p-3 rounded overflow-x-auto">
                        {JSON.stringify(selectedEndpoint.responseExample, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
              </div>
            </div>
          ) : (
            /* Code Examples Tab */
            <div className="space-y-4 lg:space-y-6">
              {['curl', 'python', 'php'].map((lang) => (
                <div key={lang} className="bg-gray-900 rounded-lg border border-gray-700 shadow-sm">
                  <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between bg-gray-800">
                    <h3 className="font-semibold text-white capitalize">{lang}</h3>
                    <button
                      onClick={() => copyToClipboard(generateCodeExample(lang))}
                      className="text-gray-400 hover:text-white flex items-center gap-1 text-sm"
                    >
                      {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                      <span className="hidden sm:inline">Copy</span>
                    </button>
                  </div>
                  <pre className="p-3 lg:p-4 text-xs sm:text-sm text-gray-300 font-mono overflow-x-auto bg-gray-900">
                    {generateCodeExample(lang)}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
